import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "../styles.css";
import { requestJson } from "../shared/public-request.mjs";
import { upcomingReveal, revealScheduleLabel, REVEAL_UNAVAILABLE, REVEAL_SUCCESS } from "../shared/reveal-landing.mjs";
import { PAGE_DEFINITIONS, pagePath, pageMetadata, applyPageMetadata, petPhotoAlt } from "../shared/site-meta.mjs";
import { containMobileMenuFocus } from "../shared/mobile-menu-focus.mjs";
import { isSensitiveUrl } from "../shared/sensitive-pages.mjs";

function capitalizePetName(value = "") {
  return String(value || "").replace(/^(\s*)(\p{Ll})/u, (_, space, letter) => space + letter.toUpperCase());
}

const IS_LOCAL_PREVIEW = window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost";
const API_BASE = IS_LOCAL_PREVIEW ? "https://thatpetname.com" : window.location.origin;
const FEATURED_VOTE_ATTRIBUTION_KEY = "tpn-featured-vote-attribution";
const FEATURED_VOTE_ATTRIBUTION_TTL_MS = 6 * 60 * 60 * 1000;

const FALLBACK_NAMES = ["Biscuit", "Winnie", "Waldo", "Champ", "Nemo", "Cobalt", "Mabel", "Gus"];
const AdminPage = lazy(() => import("./admin.jsx"));
const GENERIC_COMMUNITY_NOTES = new Set(["Vote for the name that fits best.", "Vote for the name you think is real."]);
let fallbackNameRows = null;

function attributionToken(value, maxLength = 100) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, maxLength);
}

function externalReferrerDomain(value) {
  try {
    const domain = new URL(String(value || "")).hostname.toLowerCase().replace(/^www\./, "");
    const siteDomain = window.location.hostname.toLowerCase().replace(/^www\./, "");
    return domain && domain !== siteDomain ? domain : "";
  } catch {
    return "";
  }
}

function inferredVoteSource(domain) {
  if (!domain) return "direct";
  if (/(^|\.)reddit\.com$/.test(domain)) return "reddit";
  if (/(^|\.)instagram\.com$/.test(domain)) return "instagram";
  if (/(^|\.)facebook\.com$/.test(domain)) return "facebook";
  if (/(^|\.)tiktok\.com$/.test(domain)) return "tiktok";
  if (/(^|\.)(google|bing|yahoo|duckduckgo)\.[a-z.]+$/.test(domain)) return "organic_search";
  return "referral";
}

function firstTouchVoteAttribution() {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(FEATURED_VOTE_ATTRIBUTION_KEY) || "null");
    const age = Date.now() - Number(saved?.capturedAt || 0);
    if (saved?.source && age >= 0 && age < FEATURED_VOTE_ATTRIBUTION_TTL_MS) return saved;
  } catch {
    // Continue with this visit's landing information.
  }

  const params = new URLSearchParams(window.location.search);
  const referrerDomain = externalReferrerDomain(document.referrer);
  const source = attributionToken(params.get("utm_source"), 64) || inferredVoteSource(referrerDomain);
  const medium = attributionToken(params.get("utm_medium"), 64)
    || (source === "organic_search" ? "organic" : source === "direct" ? "none" : referrerDomain ? "referral" : "");
  const attribution = {
    source,
    medium,
    campaign: attributionToken(params.get("utm_campaign"), 120),
    referrerDomain,
    landingPath: window.location.pathname.slice(0, 255) || "/",
    capturedAt: Date.now(),
  };
  try {
    window.sessionStorage.setItem(FEATURED_VOTE_ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // The attribution can still be sent with this vote when storage is unavailable.
  }
  return attribution;
}

const FEATURED_VOTE_ATTRIBUTION = firstTouchVoteAttribution();

function routeFromLocation() {
  return PAGE_DEFINITIONS[pagePath(window.location.href)]?.route || "notFound";
}

function navigateTo(href) {
  // A sensitive page must get its own document and response security policy.
  // Leaving it also restores the public shell (including submission Turnstile).
  if (isSensitiveUrl(window.location.href) || isSensitiveUrl(new URL(href, window.location.href).href)) {
    window.location.assign(href);
    return;
  }
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
}

function navigate(event, href) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  navigateTo(href);
}

async function apiRequest(path, options = {}) {
  const { timeoutMs = 20_000, ...fetchOptions } = options;
  const method = String(fetchOptions.method || "GET").toUpperCase();
  const safePreviewPost = method === "POST" && path === "/api/name-suggestions";
  if (IS_LOCAL_PREVIEW && method !== "GET" && method !== "HEAD" && !safePreviewPost) {
    throw new Error("The React preview is read-only and does not change production data.");
  }

  return requestJson(`${API_BASE}${path}`, {
    cache: "no-store",
    ...fetchOptions,
    headers: {
      Accept: "application/json",
      ...(method === "GET" || method === "HEAD" ? {} : { "Content-Type": "application/json" }),
      ...(fetchOptions.headers || {}),
    },
    timeoutMs,
  });
}

function usePublicResource(path, initialData = null) {
  const [data, setData] = useState(initialData);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!path) return;
    let active = true;
    const controller = new AbortController();
    setData(null);
    setError("");
    apiRequest(path, { signal: controller.signal }).then((result) => {
      if (active) setData(result);
    }).catch((failure) => {
      if (active) setError(failure.message);
    });
    return () => { active = false; controller.abort(); };
  }, [path, attempt]);
  const retry = () => { setError(""); setData(null); setAttempt((value) => value + 1); };
  return { data, setData, error, retry };
}

function LoadState({ error, onRetry, children }) {
  return <div className="load-state">
    <p className={error ? "form-status error" : "loading-message"} role={error ? "alert" : "status"}>
      {error || children}
    </p>
    {error && <button className="retry-button" type="button" onClick={onRetry}>Try again</button>}
  </div>;
}

function FieldError({ id, children }) {
  return <span id={id} className="field-error" aria-live="polite">{children}</span>;
}

const EMAIL_DOMAIN_TYPOS = {
  "gamil.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gmail.cmo": "gmail.com",
  "hotmial.com": "hotmail.com",
  "outlok.com": "outlook.com",
  "yaho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "iclud.com": "icloud.com",
};

function emailValidationError(email) {
  const value = String(email || "").trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Enter a valid email address.";
  const separator = value.lastIndexOf("@");
  const domain = value.slice(separator + 1).toLowerCase();
  const suggestedDomain = EMAIL_DOMAIN_TYPOS[domain]
    || (domain.endsWith(".con") ? `${domain.slice(0, -4)}.com` : "");
  return suggestedDomain ? "That email domain looks incorrect." : "";
}

function isValidEmail(email) {
  return !emailValidationError(email);
}

function formatNumber(value) {
  return String(value || "").padStart(4, "0");
}

function choiceName(choice) {
  return typeof choice === "string" ? choice : choice?.name;
}

function sameChoiceSet(first = [], second = []) {
  if (first.length !== second.length) return false;
  const firstSet = new Set(first.map(String));
  return second.every((choice) => firstSet.has(String(choice)));
}

function randomIndex(max) {
  if (globalThis.crypto?.getRandomValues) {
    const values = new Uint32Array(1);
    globalThis.crypto.getRandomValues(values);
    return values[0] % max;
  }
  return Math.floor(Math.random() * max);
}

function shuffledNames(names) {
  const shuffled = [...names];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = randomIndex(index + 1);
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
}

const publicChoiceOrderCache = new Map();

function analyticsConversionId(eventName) {
  const uniqueId = globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${eventName}:${uniqueId}`;
}

function pushAnalyticsEvent(eventName, petNumber, conversionId = "", details = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    pet_number: String(petNumber || ""),
    conversion_id: conversionId || analyticsConversionId(eventName),
    ...details,
  });
}

const REMEMBERED_REVEAL_EMAIL_KEY = "tpn-reveal-email";
const AUTO_REVEAL_EMAIL_KEY = "tpn-reveal-auto-subscribe";

function maskEmail(email) {
  const [localPart = "", domain = ""] = String(email || "").trim().split("@");
  if (!localPart || !domain) return "your saved email";
  return `${localPart.slice(0, 1)}•••@${domain}`;
}

function revealEmailAnalytics(eventName, petNumber, signupState = "") {
  return {
    eventName,
    petNumber: String(petNumber || ""),
    signupState,
    url: window.location.href,
    referrer: document.referrer,
    conversionId: analyticsConversionId(eventName),
  };
}

function trackRevealEmailEvent(eventName, petNumber, signupState = "") {
  const analytics = revealEmailAnalytics(eventName, petNumber, signupState);
  pushAnalyticsEvent(eventName, petNumber, analytics.conversionId, signupState ? { signup_state: signupState } : {});
  if (!IS_LOCAL_PREVIEW) {
    apiRequest("/api/engagement-events", {
      method: "POST",
      body: JSON.stringify({ eventName, analytics }),
    }).catch((error) => console.warn("Reveal email analytics skipped:", error.message));
  }
  return analytics;
}

function stableChoiceOrder(choices = [], orderKey) {
  const list = choices.filter(Boolean);
  const names = list.map(choiceName).filter(Boolean);
  if (names.length <= 1) return list;

  const saved = publicChoiceOrderCache.get(orderKey) || [];
  if (sameChoiceSet(saved, names)) {
    const byName = new Map(list.map((choice) => [choiceName(choice), choice]));
    return saved.map((name) => byName.get(name)).filter(Boolean);
  }

  const shuffled = shuffledNames(names);
  publicChoiceOrderCache.set(orderKey, shuffled);
  const byName = new Map(list.map((choice) => [choiceName(choice), choice]));
  return shuffled.map((name) => byName.get(name)).filter(Boolean);
}

function archiveChoiceHash(value) {
  let hash = 2166136261;
  for (const character of String(value)) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function stableArchiveChoiceOrder(choices = [], publicNumber = "") {
  return choices
    .filter(Boolean)
    .map((choice, index) => ({
      choice,
      index,
      sortKey: archiveChoiceHash(`archive:${publicNumber}:${choiceName(choice)}`),
    }))
    .sort((first, second) => first.sortKey - second.sortKey || first.index - second.index)
    .map(({ choice }) => choice);
}

function voteTotals(baseVotes = {}, selected = "") {
  const totals = { ...baseVotes };
  if (selected) totals[selected] = (Number(totals[selected]) || 0) + 1;
  return totals;
}

function percentFor(totals, choice) {
  const total = Object.values(totals).reduce((sum, value) => sum + Number(value || 0), 0);
  if (!total) return 0;
  return Math.round(((Number(totals[choice]) || 0) / total) * 100);
}

function normalizePercent(value) {
  return Math.max(0, Math.min(100, Number(value) || 0));
}

function currentLocation() {
  return {
    key: `${window.location.pathname}${window.location.search}`,
    route: routeFromLocation(),
  };
}

function useRoute() {
  const [location, setLocation] = useState(currentLocation);
  useEffect(() => {
    const onPop = () => setLocation(currentLocation());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return location;
}

function usePageMeta(pet) {
  const href = window.location.href;
  useEffect(() => {
    applyPageMetadata(document, pageMetadata(href, { pet }));
  }, [href, pet?.publicNumber]);
}

// Use this component for every public pet photo. Callers cannot accidentally
// put an answer, owner note, or name-bearing SEO field in the alt attribute.
function PetPhoto({ pet = {}, context = "daily", ...props }) {
  return <img {...props} alt={petPhotoAlt(pet, context)} />;
}

function Link({ href, children, onClick, ...props }) {
  const handleClick = (event) => {
    onClick?.(event);
    if (!event.defaultPrevented) navigate(event, href);
  };
  return (
    <a href={href} onClick={handleClick} {...props}>
      {children}
    </a>
  );
}

function Header({ route }) {
  const [open, setOpen] = useState(false);
  const headerRef = useRef(null);
  const toggleRef = useRef(null);
  useEffect(() => {
    if (open) return containMobileMenuFocus(headerRef.current, toggleRef.current, () => setOpen(false));
  }, [open]);
  useEffect(() => { setOpen(false); }, [route]);
  const isCurrent = (name) => route === name || (name === "vote" && route === "pet0000");
  const links = [
    ["vote", "/", "Vote"],
    ["past", "/past-pets", "Past Pets"],
    ["community", "/community-pets", "Community Pets"],
    ["submit", "/submit", "Submit a Pet"],
  ];

  return (
    <>
      <header ref={headerRef} className={`app-header${open ? " nav-open" : ""}`}>
        <Link className="brand" href="/" aria-label="THAT PET NAME home" onClick={() => setOpen(false)}>
          <span>THAT PET NAME</span>
          <span className="brand-explainer">A daily pet name guessing game.</span>
        </Link>
        {!open && (
          <Link className="mobile-vote-link" href="/" aria-current={isCurrent("vote") ? "page" : undefined}>
            Vote
          </Link>
        )}
        <button ref={toggleRef} className="nav-toggle" type="button" aria-expanded={open} aria-controls="primaryNav" onClick={() => setOpen(!open)}>
          <span className="sr-only">Menu</span>
          <span aria-hidden="true"></span>
        </button>
        <nav className={`top-nav${open ? " is-open" : ""}`} id="primaryNav" aria-label="Primary">
          {links.map(([name, href, label]) => (
            <Link key={name} href={href} aria-current={isCurrent(name) ? "page" : undefined} onClick={() => setOpen(false)}>
              {label}
            </Link>
          ))}
        </nav>
      </header>
    </>
  );
}

function NewsletterPetPreference({ id, value, onChange, disabled }) {
  return <div className="newsletter-pet-preference">
    <label className="sr-only" htmlFor={id}>Which pets would you like?</label>
    <select id={id} name="petPreference" value={value} onChange={onChange} disabled={disabled}>
      <option value="all">All pets</option>
      <option value="dogs">Dogs only</option>
      <option value="cats">Cats only</option>
    </select>
  </div>;
}

function SubscribePage({ reveal = false }) {
  const [petPreference, setPetPreference] = useState("all");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [tone, setTone] = useState("");
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const emailRef = useRef(null);
  const pendingRef = useRef(false);
  const [revealPet, setRevealPet] = useState(null);
  const [loadingReveal, setLoadingReveal] = useState(reveal);

  useEffect(() => {
    if (!reveal) return undefined;
    let active = true;
    setLoadingReveal(true);
    setStatus("");
    setTone("");
    apiRequest("/api/state").then((state) => {
      if (!active) return;
      const poll = upcomingReveal(state);
      setRevealPet(poll);
      if (!poll) setStatus(REVEAL_UNAVAILABLE);
    }).catch(() => {
      if (!active) return;
      setStatus("We couldn't load the next reveal. Please refresh the page to try again.");
      setTone("error");
    }).finally(() => { if (active) setLoadingReveal(false); });
    return () => { active = false; };
  }, [reveal, loadAttempt]);

  async function subscribe(event) {
    event.preventDefault();
    if (pendingRef.current || subscribed || (reveal && !revealPet)) return;
    const emailError = emailValidationError(email);
    if (emailError) {
      setStatus(emailError);
      setTone("error");
      setInvalidEmail(true);
      emailRef.current?.focus();
      return;
    }
    setInvalidEmail(false);
    pendingRef.current = true;
    setSubmitting(true);
    setStatus("");
    setTone("");
    try {
      await apiRequest(reveal ? "/api/reveal-subscriptions" : "/api/newsletter-subscriptions", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), ...(reveal ? { featuredId: revealPet.id, requireUpcomingReveal: true } : { petPreference }) }),
      });
      setSubscribed(true);
      setStatus(reveal ? REVEAL_SUCCESS : "You're on the list! Watch your inbox for That Pet Name.");
      setTone("success");
    } catch (error) {
      setStatus(error.message || "We couldn't add you right now. Please try again.");
      setTone("error");
    } finally {
      pendingRef.current = false;
      setSubmitting(false);
    }
  }

  return <main className="subscribe-page">
    <section className="subscribe-card" aria-labelledby="subscribeTitle">
      <div className="subscribe-icon" aria-hidden="true">
        <svg viewBox="0 0 48 48" fill="none"><rect x="5" y="10" width="38" height="29" rx="5" stroke="currentColor" strokeWidth="2.5" /><path d="m7 13 17 13 17-13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
      <p className="kicker">{reveal ? "You asked for the reveal" : "The weekly newsletter"}</p>
      <h1 id="subscribeTitle">{reveal ? "Get the answer in your inbox." : "Your weekly dose of pet-name fun."}</h1>
      <p className="subscribe-description">{reveal ? "Enter your email and we’ll send you today’s pet’s real name when it’s revealed." : "Get the newest pet to guess and recent reveals, delivered once a week."}</p>
      {revealPet && <p className="reveal-signup-schedule">{revealScheduleLabel(revealPet)}</p>}
      <form className="subscribe-form" noValidate onSubmit={subscribe} hidden={subscribed || (reveal && !revealPet)} aria-busy={submitting}>
        <label htmlFor="subscribeEmail">Email address</label>
        <input ref={emailRef} id="subscribeEmail" name="email" type="email" autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck={false} placeholder="you@example.com" required value={email} onChange={(event) => { setEmail(event.target.value); setInvalidEmail(false); setStatus(""); }} aria-describedby="subscribeStatus" aria-invalid={invalidEmail || undefined} disabled={submitting} />
        {!reveal && <NewsletterPetPreference id="subscribePetPreference" value={petPreference} onChange={(event) => setPetPreference(event.target.value)} disabled={submitting} />}
        <button type="submit" disabled={submitting}>{submitting ? "Signing Up..." : reveal ? "Send Me the Reveal" : "Sign Up"}</button>
      </form>
      {reveal && !revealPet && !loadingReveal && tone === "error" && <button type="button" onClick={() => setLoadAttempt((value) => value + 1)}>Try again</button>}
      <p className="subscribe-status" id="subscribeStatus" role="status" aria-live="polite" data-tone={tone}>{submitting ? "Signing you up…" : loadingReveal ? "Loading the next reveal…" : status}</p>
    </section>
  </main>;
}

function Footer() {
  const [newsletterPetPreference, setNewsletterPetPreference] = useState("all");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState("");
  const [newsletterTone, setNewsletterTone] = useState("");
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);

  const newsletterEmailRef = useRef(null);
  const [newsletterInvalid, setNewsletterInvalid] = useState(false);
  const newsletterPendingRef = useRef(false);

  async function subscribeToNewsletter(event) {
    event.preventDefault();
    if (newsletterPendingRef.current) return;
    const emailError = emailValidationError(newsletterEmail);
    if (emailError) {
      setNewsletterStatus(emailError);
      setNewsletterTone("error");
      setNewsletterInvalid(true);
      newsletterEmailRef.current?.focus();
      return;
    }

    newsletterPendingRef.current = true;
    try {
      setNewsletterSubmitting(true);
      setNewsletterInvalid(false);
      setNewsletterStatus("Signing you up…");
      setNewsletterTone("");
      await apiRequest("/api/newsletter-subscriptions", {
        method: "POST",
        body: JSON.stringify({ email: newsletterEmail, petPreference: newsletterPetPreference }),
      });
      setNewsletterEmail("");
      setNewsletterStatus("You're on the list. Watch your inbox for That Pet Name.");
      setNewsletterTone("success");
    } catch (error) {
      setNewsletterStatus(error.message || "We couldn't add you right now. Please try again.");
      setNewsletterTone("error");
    } finally {
      newsletterPendingRef.current = false;
      setNewsletterSubmitting(false);
    }
  }

  return (
    <footer className="site-footer">
      <section className="footer-newsletter" aria-labelledby="footerNewsletterTitle">
        <strong id="footerNewsletterTitle">Play Along Every Week</strong>
        <p className="footer-newsletter-copy">Get the newest pet to guess and recent reveals.</p>
        <form className="footer-newsletter-form" noValidate onSubmit={subscribeToNewsletter} aria-busy={newsletterSubmitting}>
          <label className="sr-only" htmlFor="footerNewsletterEmail">Email address</label>
          <input
            ref={newsletterEmailRef}
            aria-invalid={newsletterInvalid}
            aria-describedby="footerNewsletterStatus"
            disabled={newsletterSubmitting}
            id="footerNewsletterEmail"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={newsletterEmail}
            onChange={(event) => { setNewsletterEmail(event.target.value); setNewsletterInvalid(false); setNewsletterStatus(""); }}
            required
          />
          <NewsletterPetPreference id="footerNewsletterPetPreference" value={newsletterPetPreference} onChange={(event) => setNewsletterPetPreference(event.target.value)} disabled={newsletterSubmitting} />
          <button type="submit" disabled={newsletterSubmitting}>{newsletterSubmitting ? "Signing Up..." : "Sign Up"}</button>
        </form>
        <p id="footerNewsletterStatus" className="footer-newsletter-status" data-tone={newsletterTone} role="status" aria-live="polite">{newsletterStatus}</p>
      </section>
      <nav className="footer-column footer-play" aria-label="Play Along">
        <strong>Play Along</strong>
        <Link href="/submit">Submit a Pet</Link>
        <Link href="/past-pets">Past Pets</Link>
        <Link href="/community-pets">Community Pets</Link>
      </nav>
      <nav className="footer-column footer-support" aria-label="Support">
        <strong>Support</strong>
        <Link href="/faq">FAQs</Link>
        <Link href="/contact">Contact Us</Link>
      </nav>
      <section className="footer-connected" aria-labelledby="footerConnectedTitle">
        <strong id="footerConnectedTitle">Follow Us</strong>
        <div className="footer-social" aria-label="Social links">
          <a className="social-link" href="https://www.instagram.com/thatpetname?utm_source=qr" aria-label="Instagram">
            <img src="/assets/icon-instagram.svg" alt="" />
          </a>
          <a className="social-link" href="https://www.tiktok.com/@thatpetname?_r=1&_t=ZT-97lCDGFielx" aria-label="TikTok">
            <img src="/assets/icon-tiktok.svg" alt="" />
          </a>
        </div>
      </section>
      <div className="footer-bottom">
        <p>&copy; 2026 That Pet Name.</p>
        <nav className="footer-legal" aria-label="Legal">
          <Link href="/privacy">Privacy Policy</Link>
          <button type="button" className="tpn-privacy-choices" onClick={() => window.tpnConsent?.open()}>Privacy Choices</button>
          <Link href="/terms">Terms of Use</Link>
        </nav>
      </div>
    </footer>
  );
}

function ChoiceGrid({ choices, baseVotes, selected, submitted, busy = false, showResults = submitted, correct, onSelect, totalsIncludeSelected = false }) {
  const totals = voteTotals(baseVotes, showResults && !totalsIncludeSelected ? selected : "");
  return (
    <div className="choice-grid" aria-label="Name choices">
      {choices.map((choice) => {
        const name = choiceName(choice);
        const classes = ["choice-button"];
        const percent = showResults ? percentFor(totals, name) : 0;
        if (selected === name) classes.push("selected");
        if (correct && name === correct) classes.push("correct");
        if (correct && selected === name && name !== correct) classes.push("wrong");
        if (showResults) classes.push("has-results");
        return (
          <button
            key={name}
            type="button"
            className={classes.join(" ")}
            disabled={submitted || busy}
            aria-pressed={selected === name}
            style={{ "--choice-progress": `${normalizePercent(percent)}%` }}
            onClick={() => {
              if (!submitted) onSelect(name);
            }}
          >
            <span className="choice-name">{name}</span>
            <span className="choice-percent">{showResults ? `${percent}%` : ""}</span>
          </button>
        );
      })}
    </div>
  );
}

function ResultsBars({ choices = [], label = "Voting results", highlightCorrect = true, descending = false }) {
  const displayedChoices = descending
    ? [...choices].sort((a, b) => Number(b.percent ?? b.votes ?? 0) - Number(a.percent ?? a.votes ?? 0))
    : choices;

  return (
    <div className="reveal-results" aria-label={label}>
      {displayedChoices.map((choice) => (
        <React.Fragment key={choice.name}>
          <div className={`result-row${highlightCorrect && choice.isCorrect ? " correct" : ""}`}>
            <span>{choice.name}</span>
            <strong>{choice.percent}%</strong>
          </div>
          <div className="result-meter" aria-hidden="true">
            <span style={{ width: `${choice.percent}%` }}></span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function Pagination({ page = 1, totalPages = 1, basePath, petType = "" }) {
  if (totalPages <= 1) return null;
  const pageHref = (pageNumber) => {
    const params = new URLSearchParams();
    if (petType) params.set("petType", petType);
    if (pageNumber > 1) params.set("page", pageNumber);
    return `${basePath}${params.size ? `?${params}` : ""}`;
  };
  return (
    <nav className="pagination" aria-label="Pagination">
      {page > 1 && <Link className="pagination-arrow" href={pageHref(page - 1)} aria-label="Previous page">←</Link>}
      {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
        pageNumber === page
          ? <span key={pageNumber} className="pagination-page current" aria-current="page">{pageNumber}</span>
          : <Link key={pageNumber} className="pagination-page" href={pageHref(pageNumber)} aria-label={`Page ${pageNumber}`}>{pageNumber}</Link>
      ))}
      {page < totalPages && <Link className="pagination-arrow" href={pageHref(page + 1)} aria-label="Next page">→</Link>}
    </nav>
  );
}

function CommunityArrow({ direction }) {
  return (
    <svg className="community-arrow-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d={direction === "left" ? "M15 18l-6-6 6-6" : "M9 6l6 6-6 6"}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.8"
      />
    </svg>
  );
}

function SubmissionFollowCard() {
  return (
    <div className="follow-card submission-follow-card" aria-label="Follow That Pet Name on Instagram">
      <p>Follow <a className="submission-follow-link" href="https://www.instagram.com/thatpetname?utm_source=qr">@ThatPetName</a> on Instagram for featured pets, reveals, and more pet naming fun.</p>
    </div>
  );
}

function LockIcon({ locked }) {
  return (
    <svg className="lock-icon" aria-hidden="true" viewBox="0 0 24 24" focusable="false">
      <rect x="7" y="10" width="10" height="10" rx="2" />
      <path d={locked ? "M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10" : "M8.5 10V7.5a3.5 3.5 0 0 1 6.1-2.3"} />
    </svg>
  );
}

function TurnstileField() {
  useEffect(() => {
    if (!IS_LOCAL_PREVIEW) window.tpnTurnstile?.ready?.();
  }, []);
  if (IS_LOCAL_PREVIEW) return null;
  return <div className="turnstile-box" data-turnstile-widget></div>;
}

async function turnstileToken(form) {
  if (IS_LOCAL_PREVIEW) return "";
  await window.tpnTurnstile?.ready?.();
  const token = window.tpnTurnstile?.token?.(form) || await window.tpnTurnstile?.execute?.(form) || "";
  if (!token && window.tpnTurnstile?.status?.().required) {
    throw new Error("Verification could not be completed. Please refresh and try again, or turn off browser privacy extensions for this site.");
  }
  return token;
}

const HEIC_PHOTO_CONVERTER_MODULE = "/assets/heic-photo.js";
const IMAGE_FILE_EXTENSION = /\.(?:avif|bmp|gif|heic|heif|jpe?g|png|webp)$/i;
const HEIC_FILE_EXTENSION = /\.(?:heic|heif)$/i;

function isHeicPhoto(file) {
  const type = String(file?.type || "").toLowerCase();
  return type === "image/heic"
    || type === "image/heif"
    || type === "image/heic-sequence"
    || type === "image/heif-sequence"
    || HEIC_FILE_EXTENSION.test(String(file?.name || ""));
}

function isPetPhotoFile(file) {
  return Boolean(file && (
    String(file.type || "").toLowerCase().startsWith("image/")
    || IMAGE_FILE_EXTENSION.test(String(file.name || ""))
  ));
}

function readPhotoDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Photo could not be read."));
    reader.onload = () => resolve(String(reader.result || ""));
    reader.readAsDataURL(file);
  });
}

function decodePhoto(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Photo format could not be decoded."));
    image.src = dataUrl;
  });
}

async function compressedImageDataUrl(file, maxDimension = 1600, quality = 0.84) {
  if (!isPetPhotoFile(file)) {
    throw new Error("Choose an image file for your pet photo.");
  }

  let photoFile = file;
  let originalDataUrl = await readPhotoDataUrl(photoFile);
  let image;

  try {
    image = await decodePhoto(originalDataUrl);
  } catch (error) {
    if (!isHeicPhoto(file)) {
      throw new Error("This photo format could not be displayed. Please try another photo.");
    }

    const { convertHeicToJpeg } = await import(HEIC_PHOTO_CONVERTER_MODULE);
    photoFile = await convertHeicToJpeg(file);
    originalDataUrl = await readPhotoDataUrl(photoFile);

    try {
      image = await decodePhoto(originalDataUrl);
    } catch (conversionError) {
      throw new Error("This HEIC photo could not be converted. Please try another photo.");
    }
  }

  const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  const context = canvas.getContext("2d");
  if (!context) return originalDataUrl;

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", quality);
}

async function fallbackNamesForPronouns(pronouns) {
  if (!fallbackNameRows) {
    const response = await fetch("/data/pet-name-pool.csv", { cache: "force-cache", signal: AbortSignal.timeout(8_000) });
    if (!response.ok) return FALLBACK_NAMES;
    const text = await response.text();
    fallbackNameRows = text.split(/\r?\n/).slice(1).map((line) => {
      const [name, lean] = line.split(",");
      return { name: String(name || "").trim(), lean: String(lean || "neutral").trim() };
    }).filter((row) => row.name);
  }
  if (pronouns === "she/her") return fallbackNameRows.filter((row) => row.lean !== "boy").map((row) => row.name);
  if (pronouns === "he/him") return fallbackNameRows.filter((row) => row.lean !== "girl").map((row) => row.name);
  return fallbackNameRows.map((row) => row.name);
}

const PET0000_DEMO_PET = {
  id: "pet-0000-fred-demo",
  publicNumber: "0000",
  realName: "Fred",
  petType: "dog",
  photoUrl: "/assets/pet0000-fred.png",
  choices: ["Bentley", "Comet", "Fred", "Leo"],
  baseVotes: {
    Bentley: 74,
    Comet: 28,
    Fred: 45,
    Leo: 39,
  },
};

function VotePage({ demoPet = null }) {
  const isDemo = Boolean(demoPet);
  const { data: payload, setData: setPayload, error: loadError, retry } = usePublicResource(isDemo ? null : "/api/state", isDemo ? { current: demoPet, yesterday: null } : null);
  const current = payload?.current || null;
  const yesterday = payload?.yesterday || null;
  const choiceOrderVisitKey = useRef(`vote-${Date.now()}-${Math.random()}`).current;
  const voteKey = `tpn-name-vote:${current?.id || "loading"}`;
  const orderKey = `tpn-name-choice-order:${current?.id || "loading"}:${choiceOrderVisitKey}`;
  const emailKey = `tpn-reveal-email:${current?.id || "loading"}`;
  const [selected, setSelected] = useState("");
  const [pending, setPending] = useState("");
  const [email, setEmail] = useState("");
  const [rememberedEmail, setRememberedEmail] = useState("");
  const [subscribedEmail, setSubscribedEmail] = useState("");
  const [editingRevealEmail, setEditingRevealEmail] = useState(false);
  const [autoRevealEmail, setAutoRevealEmail] = useState(false);
  const [revealSignupDismissed, setRevealSignupDismissed] = useState(false);
  const [emailStatus, setEmailStatus] = useState("");
  const [emailStatusIsError, setEmailStatusIsError] = useState(false);
  const [revealEmailInvalid, setRevealEmailInvalid] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const revealPendingRef = useRef(false);
  const [countdown, setCountdown] = useState(isDemo ? "23h 59m" : "");
  const [voteStatus, setVoteStatus] = useState("");
  const [voting, setVoting] = useState(false);
  const votePendingRef = useRef(false);
  const [totalsIncludeSelected, setTotalsIncludeSelected] = useState(false);
  const revealSignupRef = useRef(null);
  const revealCtaViewKeyRef = useRef("");
  const revealCtaStartedRef = useRef(false);
  const submittedThisVisitRef = useRef(false);
  const resultsEmailGateActive = Boolean(current?.resultsEmailGate) && !isDemo;
  // Server recognition verifies that the address is still on the active list.
  // A locally remembered address alone must not bypass the results gate.
  const recognizedSubscriber = Boolean(current?.subscriberRecognized);
  const resultsUnlocked = !resultsEmailGateActive || recognizedSubscriber || Boolean(subscribedEmail);
  const gateNeedsEmail = resultsEmailGateActive && !recognizedSubscriber && !subscribedEmail;
  const showRevealSignup = Boolean(
    selected && !revealSignupDismissed
    && (!resultsEmailGateActive || !recognizedSubscriber || subscribedEmail),
  );

  useEffect(() => {
    if (!current?.id) return;
    if (isDemo) {
      setSelected("");
      setPending("");
      setEmail("");
      setRememberedEmail("");
      setSubscribedEmail("");
      setEditingRevealEmail(true);
      setAutoRevealEmail(false);
      setRevealSignupDismissed(false);
      setEmailStatus("");
      setEmailStatusIsError(false);
      setTotalsIncludeSelected(false);
      revealCtaViewKeyRef.current = "";
      revealCtaStartedRef.current = false;
      return;
    }
    const storedVote = localStorage.getItem(voteKey) || "";
    const params = new URLSearchParams(window.location.search);
    const requestedName = String(params.get("pick") || "").trim();
    const requestedPet = String(params.get("pet") || "").trim();
    const requestedChoice = (current.choices || []).find(
      (choice) => choice.toLowerCase() === requestedName.toLowerCase(),
    ) || "";
    const requestedPetMatches = !requestedPet
      || requestedPet === current.id
      || requestedPet === current.publicNumber;

    const pollEmail = localStorage.getItem(emailKey) || "";
    const savedEmail = localStorage.getItem(REMEMBERED_REVEAL_EMAIL_KEY) || pollEmail;
    if (pollEmail && !localStorage.getItem(REMEMBERED_REVEAL_EMAIL_KEY)) {
      localStorage.setItem(REMEMBERED_REVEAL_EMAIL_KEY, pollEmail);
    }

    setSelected(storedVote);
    setTotalsIncludeSelected(!IS_LOCAL_PREVIEW);
    setPending(!storedVote && requestedPetMatches ? requestedChoice : "");
    setEmail(savedEmail);
    setRememberedEmail(savedEmail);
    setSubscribedEmail(pollEmail);
    setEditingRevealEmail(!savedEmail);
    setAutoRevealEmail(localStorage.getItem(AUTO_REVEAL_EMAIL_KEY) === "true");
    setRevealSignupDismissed(sessionStorage.getItem(`tpn-reveal-dismissed:${current.id}`) === "true");
    setEmailStatus("");
    setEmailStatusIsError(false);
    revealCtaViewKeyRef.current = "";
    revealCtaStartedRef.current = false;
  }, [current?.choices, current?.id, current?.publicNumber, emailKey, isDemo, voteKey]);

  useEffect(() => {
    if (!selected || revealSignupDismissed || !current?.id) return;
    if (!isDemo && !subscribedEmail && (!resultsEmailGateActive || gateNeedsEmail)) {
      const signupState = rememberedEmail && !editingRevealEmail ? "remembered" : "first_time";
      const viewKey = `${current.id}:${signupState}`;
      if (revealCtaViewKeyRef.current !== viewKey) {
        revealCtaViewKeyRef.current = viewKey;
        trackRevealEmailEvent("reveal_email_cta_viewed", current.publicNumber, signupState);
      }
    }

    if (!submittedThisVisitRef.current || !revealSignupRef.current) return;
    submittedThisVisitRef.current = false;
    window.requestAnimationFrame(() => {
      const bounds = revealSignupRef.current?.getBoundingClientRect();
      if (bounds && bounds.bottom > window.innerHeight - 16) {
        revealSignupRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    });
  }, [current?.id, current?.publicNumber, editingRevealEmail, gateNeedsEmail, isDemo, rememberedEmail, resultsEmailGateActive, revealSignupDismissed, selected, subscribedEmail]);

  useEffect(() => {
    if (isDemo) return undefined;
    if (!current?.revealAt) return undefined;
    const tick = () => {
      const revealAt = new Date(current.revealAt).getTime();
      const diff = revealAt - Date.now();
      if (diff <= 0) {
        setCountdown("now");
        return;
      }
      const hours = Math.floor(diff / 3_600_000);
      const minutes = Math.floor((diff % 3_600_000) / 60_000);
      setCountdown(`${hours}h ${minutes}m`);
    };
    tick();
    const timer = window.setInterval(tick, 60_000);
    return () => window.clearInterval(timer);
  }, [current?.revealAt, isDemo]);

  const orderedChoices = useMemo(
    () => isDemo ? (current?.choices || []) : stableChoiceOrder(current?.choices || [], orderKey),
    [current?.choices, current?.id, isDemo, orderKey],
  );
  async function subscribeForReveal(address, source) {
    if (revealPendingRef.current) return false;
    const normalizedEmail = String(address || "").trim().toLowerCase();
    const emailError = emailValidationError(normalizedEmail);
    if (emailError) {
      setEmailStatus(emailError);
      setEmailStatusIsError(true);
      setRevealEmailInvalid(true);
      document.getElementById("revealEmail")?.focus();
      return false;
    }

    setRevealEmailInvalid(false);
    setEmailStatus("Saving your reveal delivery…");
    setEmailStatusIsError(false);

    if (isDemo) {
      setEmail(normalizedEmail);
      setSubscribedEmail(normalizedEmail);
      setEditingRevealEmail(false);
      setRevealSignupDismissed(false);
      setEmailStatus(`Tomorrow’s reveal will go to ${maskEmail(normalizedEmail)}.`);
      return true;
    }

    revealPendingRef.current = true;
    setEmailSubmitting(true);
    const eventName = `reveal_email_${source}`;
    const analytics = revealEmailAnalytics(eventName, current.publicNumber, source);
    try {
      if (!IS_LOCAL_PREVIEW) {
        await apiRequest("/api/reveal-subscriptions", {
          method: "POST",
          body: JSON.stringify({ featuredId: current.id, email: normalizedEmail, analytics }),
        });
      }

      localStorage.setItem(emailKey, normalizedEmail);
      localStorage.setItem(REMEMBERED_REVEAL_EMAIL_KEY, normalizedEmail);
      if (source !== "auto") {
        if (autoRevealEmail) localStorage.setItem(AUTO_REVEAL_EMAIL_KEY, "true");
        else localStorage.removeItem(AUTO_REVEAL_EMAIL_KEY);
      }

      setEmail(normalizedEmail);
      setRememberedEmail(normalizedEmail);
      setSubscribedEmail(normalizedEmail);
      setEditingRevealEmail(false);
      setRevealSignupDismissed(false);
      setEmailStatus(`Tomorrow’s reveal will go to ${maskEmail(normalizedEmail)}.`);

      if (source === "first_time" || source === "changed") {
        pushAnalyticsEvent("reveal_email_subscribed", current.publicNumber);
      }
      pushAnalyticsEvent(eventName, current.publicNumber, analytics.conversionId, { signup_state: source });
      return true;
    } catch (error) {
      setEmailStatus(error.message || "We could not save your email. Please try again.");
      setEmailStatusIsError(true);
      return false;
    } finally {
      revealPendingRef.current = false;
      setEmailSubmitting(false);
    }
  }

  function trackRevealEmailStart() {
    if (isDemo) return;
    if (revealCtaStartedRef.current) return;
    revealCtaStartedRef.current = true;
    trackRevealEmailEvent("reveal_email_started", current.publicNumber, rememberedEmail ? "changed" : "first_time");
  }

  function dismissRevealSignup() {
    setRevealSignupDismissed(true);
    if (isDemo) return;
    sessionStorage.setItem(`tpn-reveal-dismissed:${current.id}`, "true");
    trackRevealEmailEvent(
      "reveal_email_dismissed",
      current.publicNumber,
      rememberedEmail && !editingRevealEmail ? "remembered" : "first_time",
    );
  }

  function useDifferentRevealEmail() {
    setEmail("");
    setEmailStatus("");
    setEmailStatusIsError(false);
    setEditingRevealEmail(true);
    revealCtaStartedRef.current = false;
  }

  function turnOffAutomaticReveal() {
    setAutoRevealEmail(false);
    if (isDemo) return;
    localStorage.removeItem(AUTO_REVEAL_EMAIL_KEY);
    trackRevealEmailEvent("reveal_email_auto_disabled", current.publicNumber, "auto_subscribed");
  }

  async function submitVote() {
    if (!pending || selected || votePendingRef.current) return;
    setVoteStatus("");
    submittedThisVisitRef.current = true;

    if (isDemo) {
      setSelected(pending);
      setTotalsIncludeSelected(false);
      return;
    }

    if (IS_LOCAL_PREVIEW) {
      setSelected(pending);
      setTotalsIncludeSelected(false);
      localStorage.setItem(voteKey, pending);
      if (autoRevealEmail && rememberedEmail) {
        await subscribeForReveal(rememberedEmail, "auto_subscribed");
      }
      return;
    }

    votePendingRef.current = true;
    setVoting(true);
    try {
      const conversionId = analyticsConversionId("featured_pet_vote_submitted");
      const result = await apiRequest("/api/votes", {
        method: "POST",
        body: JSON.stringify({
          featuredId: current.id,
          choice: pending,
          voterKey: getVoterKey(),
          attribution: FEATURED_VOTE_ATTRIBUTION,
          analytics: {
            url: window.location.href,
            referrer: document.referrer,
            conversionId,
          },
        }),
      });
      setPayload((existing) => ({
        ...existing,
        current: { ...existing.current, baseVotes: result.totals || existing.current.baseVotes },
      }));
      setSelected(pending);
      setTotalsIncludeSelected(true);
      setVoting(false);
      localStorage.setItem(voteKey, pending);
      pushAnalyticsEvent("featured_pet_vote_ad_conversion", current.publicNumber, conversionId);
      if (autoRevealEmail && rememberedEmail) {
        await subscribeForReveal(rememberedEmail, "auto_subscribed");
      }
    } catch (error) {
      submittedThisVisitRef.current = false;
      setVoteStatus(error.message || "Your vote could not be saved. Please try again.");
    } finally {
      votePendingRef.current = false;
      setVoting(false);
    }
  }

  async function saveRevealEmail(event) {
    event.preventDefault();
    await subscribeForReveal(email, rememberedEmail ? "changed" : "first_time");
  }

  async function saveRememberedRevealEmail(event) {
    event.preventDefault();
    await subscribeForReveal(rememberedEmail, "reused");
  }

  if (!payload) {
    return (
      <main>
        <section className="game-shell">
          <LoadState error={loadError} onRetry={retry}>Loading today’s featured pet…</LoadState>
        </section>
      </main>
    );
  }

  if (!current) {
    return (
      <main>
        <section className="game-shell archive-break">
          <div className="game-intro">
            <p className="kicker">Past Pets</p>
            <h1>Keep playing with Past Pets</h1>
            <p className="pet-note">Daily voting is taking a break. Guess a pet’s name, see the answer, and play the next one.</p>
            {payload.yesterday && <p>Latest reveal: <strong>{payload.yesterday.petName}</strong> · <Link href={`/pet?id=${payload.yesterday.publicNumber}`}>See the results</Link></p>}
          </div>
          {payload.archivePet && <div className="archive-break-spotlight"><ArchiveCard pet={payload.archivePet} /></div>}
          <Link className="button-link secondary" href="/past-pets">Browse all Past Pets</Link>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section id="game" className="game-shell">
        <div className="game-intro" aria-labelledby="dailyTitle">
          <div className="kicker">{current.publicNumber}</div>
          <h1 id="dailyTitle">What's my name?</h1>
          {current.clue && <p className="pet-note">{current.clue}</p>}
        </div>
        <div className="pet-stage">
          <div className="pet-media">
            {current.photoUrl && <PetPhoto pet={current}
              src={current.photoUrl}
              srcSet={current.photoSrcSet || undefined}
              data-pet-number={current.publicNumber}
              decoding="async"
              fetchPriority="high"
            />}
            <div className="daily-badge">
              <span>Time left</span>
              <strong>Reveal {countdown}</strong>
            </div>
          </div>
          <aside className="game-panel" aria-label="Vote on the pet name">
            <h2 className="vote-heading">Guess my real name</h2>
            <ChoiceGrid
              choices={orderedChoices}
              baseVotes={current.baseVotes || {}}
              selected={selected || pending}
              submitted={Boolean(selected)}
              busy={voting}
              showResults={Boolean(selected) && resultsUnlocked}
              correct={isDemo ? "" : current.realName}
              onSelect={setPending}
              totalsIncludeSelected={totalsIncludeSelected}
            />
            <button className="vote-submit" type="button" disabled={Boolean(selected) || !pending || voting} aria-busy={voting} onClick={submitVote}>
              {voting ? "Submitting…" : selected ? "Your Vote is In!" : "Cast My Vote"}
            </button>
            <p className={`form-status${voteStatus ? " error" : ""}`} role="status">{voting ? "Saving your vote…" : voteStatus}</p>
            {showRevealSignup && (
              <section
                className={`reveal-signup${subscribedEmail ? " is-saved" : ""}`}
                ref={revealSignupRef}
                aria-label="Tomorrow’s reveal"
                aria-live="polite"
              >
                {subscribedEmail ? (
                  <>
                    <p className="reveal-signup-title">Tomorrow’s reveal is on the way.</p>
                    <div id="revealEmailStatus" className={`signup-message${emailStatusIsError ? " error" : ""}`} role="status">
                      {emailStatus || `We’ll send it to ${maskEmail(subscribedEmail)}.`}
                    </div>
                    {autoRevealEmail && (
                      <button className="reveal-secondary-action" type="button" onClick={turnOffAutomaticReveal}>
                        Turn off automatic delivery
                      </button>
                    )}
                  </>
                ) : rememberedEmail && !editingRevealEmail ? (
                  <form noValidate onSubmit={saveRememberedRevealEmail}>
                    <p className="reveal-signup-title">Send tomorrow’s reveal to {maskEmail(rememberedEmail)}?</p>
                    <label className="reveal-auto-choice">
                      <input type="checkbox" checked={autoRevealEmail} onChange={(event) => setAutoRevealEmail(event.target.checked)} />
                      <span>Automatically send me the reveal whenever I vote on this device.</span>
                    </label>
                    <div className="reveal-actions">
                      <button type="submit">Yes, send it</button>
                      <button className="reveal-secondary-action" type="button" disabled={emailSubmitting} onClick={useDifferentRevealEmail}>Use another email</button>
                    </div>
                    <div id="revealEmailStatus" className={`signup-message${emailStatusIsError ? " error" : ""}`} role="status">{emailStatus}</div>
                  </form>
                ) : gateNeedsEmail ? (
                  <form noValidate onSubmit={saveRevealEmail} aria-busy={emailSubmitting}>
                    <p className="reveal-signup-title">See how everyone voted</p>
                    <p className="reveal-gate-copy">Enter your email to see the results and join the party.</p>
                    <div className="email-row">
                      <label htmlFor="revealEmail" className="sr-only">Email address</label>
                      <input
                        id="revealEmail"
                        aria-describedby="revealEmailStatus"
                        aria-invalid={revealEmailInvalid}
                        disabled={emailSubmitting}
                        type="email"
                        placeholder="you@example.com"
                        autoComplete="email"
                        value={email}
                        onFocus={trackRevealEmailStart}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          setRevealEmailInvalid(false);
                          setEmailStatus("");
                          setEmailStatusIsError(false);
                        }}
                      />
                      <button type="submit" disabled={emailSubmitting}>{emailSubmitting ? "Saving…" : "Unlock the Results"}</button>
                    </div>
                    <p className="reveal-gate-helper">Already subscribed? Enter the same email once and we’ll remember you on this device.</p>
                    <label className="reveal-auto-choice">
                      <input type="checkbox" checked={autoRevealEmail} onChange={(event) => setAutoRevealEmail(event.target.checked)} />
                      <span>Automatically send me the reveal whenever I vote on this device.</span>
                    </label>
                    <div id="revealEmailStatus" className={`signup-message${emailStatusIsError ? " error" : ""}`} role="status">{emailStatus}</div>
                  </form>
                ) : (
                  <form noValidate onSubmit={saveRevealEmail} aria-busy={emailSubmitting}>
                    <p className="reveal-signup-title">Want the answer tomorrow?</p>
                    <div className="email-row">
                      <label htmlFor="revealEmail" className="sr-only">Email address</label>
                      <input
                        id="revealEmail"
                        aria-describedby="revealEmailStatus"
                        aria-invalid={revealEmailInvalid}
                        disabled={emailSubmitting}
                        type="email"
                        placeholder="you@example.com"
                        autoComplete="email"
                        value={email}
                        onFocus={trackRevealEmailStart}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          setRevealEmailInvalid(false);
                          setEmailStatus("");
                          setEmailStatusIsError(false);
                        }}
                      />
                      <button type="submit" disabled={emailSubmitting}>{emailSubmitting ? "Saving…" : "Send it to me"}</button>
                    </div>
                    <label className="reveal-auto-choice">
                      <input type="checkbox" checked={autoRevealEmail} onChange={(event) => setAutoRevealEmail(event.target.checked)} />
                      <span>Automatically send me the reveal whenever I vote on this device.</span>
                    </label>
                    <div id="revealEmailStatus" className={`signup-message${emailStatusIsError ? " error" : ""}`} role="status">{emailStatus}</div>
                  </form>
                )}
              </section>
            )}
            <div className="result-panel" aria-live="polite">
              {selected && !resultsUnlocked
                ? "Enter your email above to unlock today’s live results."
                : selected
                  ? `Truth drops in ${countdown}.`
                  : "The truth stays sealed until the time is up."}
            </div>
          </aside>
        </div>
        {yesterday && <section className="yesterday-reveal" aria-labelledby="yesterdayRevealTitle">
          <div className="reveal-copy">
            <p className="kicker">{yesterday.publicNumber}</p>
            <h2 id="yesterdayRevealTitle">Yesterday&rsquo;s Reveal</h2>
            <p>The answer was <strong>{yesterday.petName}</strong>.</p>
            {yesterday.clue && <p className="pet-detail-clue">{yesterday.clue}</p>}
          </div>
          <div className="reveal-detail-grid">
            <ResultsBars choices={yesterday.choices || []} label="Yesterday's voting results" descending />
            <figure className="reveal-photo">
              {yesterday.photoUrl && <PetPhoto pet={yesterday}
                src={yesterday.photoUrl}
                srcSet={yesterday.photoSrcSet || undefined}
                data-pet-number={yesterday.publicNumber}
                decoding="async"
                loading="lazy"
              />}
            </figure>
          </div>
          <Link className="reveal-archive-link" href="/past-pets">View Past Pets</Link>
        </section>}
        <section className="home-submit-pet" aria-labelledby="homeSubmitPetTitle">
          <p className="kicker">Have a pet?</p>
          <h2 id="homeSubmitPetTitle">Submit your own pet</h2>
          <p>Send us a favorite photo and their real name for a future round.</p>
          <Link className="button-link home-community-link" href="/submit">Submit a Pet</Link>
        </section>
        <section className="home-community" aria-labelledby="homeCommunityTitle">
          <p className="kicker">Community Pets</p>
          <h2 id="homeCommunityTitle">Want more pets to vote on?</h2>
          <p>Check out live community polls and help pick names.</p>
          <Link className="button-link home-community-link" href="/community-pets">View Community Pets</Link>
        </section>
      </section>
    </main>
  );
}

function WelcomePage() {

  const exampleNames = ["Fred", "Murray", "Comet", "Otis"];

  return (
    <main>
      <section className="welcome-intro" aria-labelledby="welcomeTitle">
        <h1 id="welcomeTitle">Welcome to That Pet Name</h1>
        <p className="welcome-copy">See how the game works, then send us your dog for a chance to become a Daily Pet.</p>
      </section>

      <section className="welcome-layout">
        <div id="examplePoll" className="welcome-example-poll">
          <div className="game-intro" aria-labelledby="examplePollTitle">
            <div className="kicker">Example Poll · 0000</div>
            <h2 id="examplePollTitle">What's my name?</h2>
          </div>

          <div className="pet-stage">
            <div className="pet-media welcome-demo-photo">
              <img src="/assets/social-preview-whats-my-name.jpg" alt="Golden dog on a patio chair beneath What's my name text" />
              <div className="welcome-demo-overlay" aria-label="Example name poll">
                <div className="choice-grid" aria-label="Example name choices">
                  {exampleNames.map((name) => (
                    <div className="choice-button welcome-example-choice" key={name}>
                      <span className="choice-name">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <Link className="button-link welcome-live-link" href="/">Play today&rsquo;s Daily Pet</Link>
          </div>
        </div>

        <aside className="welcome-submit-prompt" aria-labelledby="welcomeSubmitTitle">
          <div>
            <p className="kicker">Your dog could be next</p>
            <h2 id="welcomeSubmitTitle">Think we can guess their name?</h2>
            <p>Share a photo and the real name. We&rsquo;ll create the other guesses and may feature your dog in a Daily Pet poll.</p>
          </div>
          <Link className="button-link" href="/submit">Submit your dog</Link>
          <div className="follow-card welcome-follow-card" aria-label="Follow That Pet Name">
            <p>Follow @thatpetname</p>
            <div className="follow-links">
              <a href="https://www.instagram.com/thatpetname?utm_source=qr" aria-label="Follow @thatpetname on Instagram">
                <img src="/assets/icon-instagram.svg" alt="" />
                <span>Instagram</span>
              </a>
              <a href="https://www.tiktok.com/@thatpetname?_r=1&_t=ZT-97lCDGFielx" aria-label="Follow @thatpetname on TikTok">
                <img src="/assets/icon-tiktok.svg" alt="" />
                <span>TikTok</span>
              </a>
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
}

function getVoterKey() {
  const key = "that_pet_name_voter";
  let existing = localStorage.getItem(key);
  if (!existing) {
    existing = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    localStorage.setItem(key, existing);
  }
  return existing;
}

function CommunityPetsPage() {
  const choiceOrderVisitKey = useRef(`community-list-${Date.now()}-${Math.random()}`).current;
  const { data: payload, error: loadError, retry } = usePublicResource("/api/community-polls");
  const [filter, setFilter] = useState("all");
  const [visible, setVisible] = useState(12);

  const polls = (payload?.polls || []).filter((poll) => filter === "all" || poll.pollType === filter);
  const visiblePolls = polls.slice(0, visible);

  return (
    <main>
      <section className="section-band page-shell">
        <div className="section-heading">
          <div>
            <p className="kicker">Live Polls</p>
            <h1>Community Pets</h1>
          </div>
        </div>
        <div className="community-filter" role="group" aria-label="Filter community polls">
          {["all", "guess", "help"].map((value) => (
            <button key={value} type="button" aria-pressed={filter === value} onClick={() => { setFilter(value); setVisible(12); }}>
              {value === "all" ? "All" : value === "guess" ? "Guess Pet's Real Name" : "Help Name This Pet"}
            </button>
          ))}
        </div>
        <div className="community-grid">
          {visiblePolls.map((poll) => <CommunityPollCard key={poll.id} poll={poll} choiceOrderVisitKey={choiceOrderVisitKey} />)}
          {!payload && <LoadState error={loadError} onRetry={retry}>Loading community pets…</LoadState>}
          {payload && !visiblePolls.length && <p className="admin-empty" role="status">{filter === "all" ? "No community polls yet." : "No community polls match this filter."}</p>}
        </div>
        {polls.length > visible && (
          <button className="show-more-button" type="button" onClick={() => setVisible(visible + 12)}>Show More</button>
        )}
      </section>
    </main>
  );
}

function CommunityPollCard({ poll, choiceOrderVisitKey }) {
  const href = `/community-pet?id=${encodeURIComponent(poll.id)}`;
  const orderedChoices = useMemo(
    () => stableChoiceOrder(poll.choices || [], `community_card_choice_order_${poll.id}:${choiceOrderVisitKey}`),
    [poll.id, poll.choices, choiceOrderVisitKey],
  );
  return (
    <article className="community-card">
      <Link className="community-card-image-link" href={href}>
        <PetPhoto pet={poll} context="community"
          src={poll.photoUrl || "/assets/favicon.png"}
          srcSet={poll.photoSrcSet || undefined}
          decoding="async"
          loading="lazy"
        />
      </Link>
      <div className="community-card-body">
        <p className="kicker">{poll.pollType === "guess" ? "Guess pet's real name" : "Help name this pet"}</p>
        <h2>
          <Link href={href}>{poll.pollType === "guess" ? "What's their real name?" : "Which name fits best?"}</Link>
        </h2>
        {poll.personality && !GENERIC_COMMUNITY_NOTES.has(String(poll.personality).trim()) && <p className="archive-clue">{poll.personality}</p>}
        <div className="community-card-poll">
          <h3>Name options</h3>
          <div className="community-card-name-options">
            {orderedChoices.map((choice) => (
              <span key={choice.name} className="mini-option community-card-name-option">{choice.name}</span>
            ))}
          </div>
          <div className="community-card-actions">
            <Link className="button-link community-card-vote-link" href={href}>Click to Vote</Link>
          </div>
        </div>
      </div>
    </article>
  );
}

function CommunityPetPage() {
  const params = new URLSearchParams(window.location.search);
  const pollId = params.get("id") || "";
  const choiceOrderVisitKey = useRef(`community-${Date.now()}-${Math.random()}`).current;
  const cameFromSubmit = params.get("submitted") === "1";
  const { data: poll, setData: setPoll, error: loadError, retry } = usePublicResource(`/api/community-poll?id=${encodeURIComponent(pollId)}`);
  const [selected, setSelected] = useState("");
  const [savedVote, setSavedVote] = useState(() => localStorage.getItem(`community_vote_${pollId}`) || "");
  const [savedResult, setSavedResult] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`community_vote_result_${pollId}`) || "null");
    } catch {
      return null;
    }
  });
  const [status, setStatus] = useState("");
  const [voting, setVoting] = useState(false);
  const votePendingRef = useRef(false);
  const [shareStatus, setShareStatus] = useState("");



  const visiblePoll = savedResult?.id === pollId ? { ...poll, ...savedResult } : poll;
  const orderedChoices = useMemo(
    () => stableChoiceOrder(visiblePoll?.choices || [], `community_choice_order_${pollId}:${choiceOrderVisitKey}`),
    [visiblePoll, pollId, choiceOrderVisitKey],
  );

  async function submitVote() {
    if (!selected || !poll || savedVote || votePendingRef.current) return;
    votePendingRef.current = true;
    setVoting(true);
    setStatus("");
    try {
      const updated = await apiRequest("/api/community-votes", {
        method: "POST",
        body: JSON.stringify({ pollId: poll.id, choice: selected, voterKey: getVoterKey() }),
      });
      localStorage.setItem(`community_vote_${poll.id}`, selected);
      localStorage.setItem(`community_vote_result_${poll.id}`, JSON.stringify(updated));
      setSavedVote(selected);
      setSavedResult(updated);
      setPoll(updated);
    } catch (error) {
      setStatus(error.message);
    } finally {
      votePendingRef.current = false;
      setVoting(false);
    }
  }

  async function sharePoll() {
    const url = new URL(window.location.href);
    url.searchParams.delete("submitted");
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${poll.pollType === "guess" ? "Guess this" : "Help name this"} ${String(poll.petType || "pet").toLowerCase()}`,
          text: poll.pollType === "guess" ? "Vote on this pet's real name." : "Vote on the name for this pet.",
          url: url.href,
        });
        setShareStatus("Share sheet opened.");
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url.href);
        setShareStatus("Poll link copied.");
      } else {
        setShareStatus("Copy the link and share it anywhere.");
      }
    } catch (error) {
      if (error.name !== "AbortError") setShareStatus("Copy the link and share it anywhere.");
    }
  }

  if (!poll) {
    return <main><section className="section-band page-shell"><LoadState error={loadError} onRetry={retry}>Loading community poll…</LoadState></section></main>;
  }

  const hasVoted = Boolean(savedVote);
  const isGuess = visiblePoll.pollType === "guess";

  if (cameFromSubmit) {
    const pollUrl = `/community-pet?id=${encodeURIComponent(poll.id)}`;
    return (
      <main>
        <section className="section-band page-shell">
          <div className="community-detail-shell">
            <article className="legal-page community-live-confirmation">
              <p className="kicker">Submitted</p>
              <h1>Your poll is live</h1>
              <p>Share this link with friends, family, group chats, and social posts so people can vote on the name.</p>
              <div className="share-link-box">
                <Link className="poll-text-link" href={pollUrl}>View your Poll</Link>
                <div className="share-actions">
                  <button className="share-poll-button" type="button" onClick={sharePoll}>
                    <img className="share-button-icon" src="/assets/icon-share.png" alt="" /> Share Poll
                  </button>
                </div>
              </div>
              <p className="signup-message" role="status">{shareStatus}</p>
              <SubmissionFollowCard />
            </article>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section className="section-band page-shell">
        <div className="community-detail-shell">
          <div className="community-detail-hero">
            <div className="pet-detail-copy">
              <p className="kicker">Community Pets</p>
              <h1>{isGuess ? `Guess this ${String(poll.petType || "pet").toLowerCase()}'s real name` : `Help name this ${String(poll.petType || "pet").toLowerCase()}`}</h1>
              {poll.personality && !GENERIC_COMMUNITY_NOTES.has(String(poll.personality).trim()) && <p className="pet-detail-clue">{poll.personality}</p>}
              <div className="community-detail-actions">
                <button className="share-poll-button" type="button" onClick={sharePoll}>
                  <img className="share-button-icon" src="/assets/icon-share.png" alt="" /> Share Poll
                </button>
                <p className="signup-message community-share-status" role="status">{shareStatus}</p>
              </div>
            </div>
            <figure className="pet-detail-photo">
              <PetPhoto pet={poll} context="community"
                src={poll.photoUrl || "/assets/favicon.png"}
                srcSet={poll.photoSrcSet || undefined}
                decoding="async"
                fetchPriority="high"
              />
            </figure>
          </div>
          <section className="pet-detail-card community-vote-card">
            <h2>{hasVoted ? "Current Results" : "Pick a name"}</h2>
            {hasVoted && <p className="archive-clue community-result-total">{visiblePoll.totalVotes} total vote{visiblePoll.totalVotes === 1 ? "" : "s"}</p>}
            <div className="choice-grid community-choice-grid">
              {orderedChoices.map((choice) => {
                const classes = ["choice-button"];
                const percent = normalizePercent(choice.percent);
                if ((selected || savedVote) === choice.name) classes.push("selected");
                if (hasVoted && isGuess && choice.isCorrect) classes.push("correct");
                if (hasVoted && isGuess && savedVote === choice.name && choice.isCorrect === false) classes.push("wrong");
                if (hasVoted) classes.push("has-results");
                return (
                  <button
                    key={choice.name}
                    type="button"
                    className={classes.join(" ")}
                    disabled={hasVoted || voting}
                    aria-pressed={(selected || savedVote) === choice.name}
                    style={{ "--choice-progress": `${hasVoted ? percent : 0}%` }}
                    onClick={() => {
                      if (!hasVoted) setSelected(choice.name);
                    }}
                  >
                    <span className="choice-name">{choice.name}</span>
                    <span className="choice-percent">{hasVoted ? `${percent}%` : ""}</span>
                  </button>
                );
              })}
            </div>
            <button className="vote-submit" type="button" disabled={hasVoted || !selected || voting} aria-busy={voting} onClick={submitVote}>
              {voting ? "Submitting…" : hasVoted ? "Your Vote is In!" : "Submit"}
            </button>
            {!hasVoted && (
              <p className="archive-clue community-vote-help">
                {isGuess ? "Current results and the truth available after you vote." : "Current results available after you vote."}
              </p>
            )}
            {hasVoted && isGuess && visiblePoll.realName && <p className="archive-clue">The real name is {visiblePoll.realName}.</p>}
            <p className={`form-status${status ? " error" : ""}`} role="status">{voting ? "Saving your vote…" : status}</p>
          </section>
          {(poll.previousPoll || poll.nextPoll) && (
            <nav className="pet-neighbor-nav" aria-label="Community pet navigation">
              {poll.previousPoll && (
                <Link
                  className="button-link pet-nav-link community-arrow-link"
                  href={poll.previousPoll.url || `/community-pet?id=${encodeURIComponent(poll.previousPoll.id)}`}
                  aria-label="Back to the previous community pet"
                  title="Back"
                >
                  <CommunityArrow direction="left" />
                </Link>
              )}
              {poll.nextPoll && (
                <Link
                  className="button-link pet-nav-link community-arrow-link"
                  href={poll.nextPoll.url || `/community-pet?id=${encodeURIComponent(poll.nextPoll.id)}`}
                  aria-label="Forward to the next community pet"
                  title="Forward"
                >
                  <CommunityArrow direction="right" />
                </Link>
              )}
            </nav>
          )}
          <Link className="pet-archive-text-link" href="/community-pets">&lt; Return to Community Pets</Link>
        </div>
      </section>
    </main>
  );
}

function PastPetsPage() {
  const params = new URLSearchParams(window.location.search);
  const petType = ["Dog", "Cat", "Other"].find((type) => type.toLowerCase() === (params.get("petType") || "").toLowerCase()) || "";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const { data: payload, error: loadError, retry } = usePublicResource(`/api/archive?page=${page}&pageSize=12${petType ? `&petType=${petType}` : ""}`);

  return (
    <main>
      <section className="section-band page-shell">
        <div className="section-heading submit-heading archive-heading">
          <div>
            <p className="kicker">Archive</p>
            <h1>Past Pets</h1>
          </div>
          <p className="section-copy">Choose a past pet to play, or jump straight to the original reveal.</p>
        </div>
        <div className="archive-filter-row"><label htmlFor="archive-pet-type"><span>Pet Type</span><select id="archive-pet-type" value={petType} onChange={(event) => navigateTo(event.target.value ? `/past-pets?petType=${event.target.value}` : "/past-pets")}><option value="">All pet types</option><option value="Dog">Dogs</option><option value="Cat">Cats</option><option value="Other">Other</option></select></label></div>
        <div className="archive-grid" aria-live="polite">
          {(payload?.pets || []).map((pet) => <ArchiveCard key={pet.id} pet={pet} />)}
          {!payload && <LoadState error={loadError} onRetry={retry}>Loading past pets…</LoadState>}
          {payload && !payload.pets?.length && <p className="admin-empty" role="status">{petType ? "No past pets match this filter." : "No pets have been revealed yet."}</p>}
        </div>
        <Pagination page={payload?.page || page} totalPages={payload?.totalPages || 1} basePath="/past-pets" petType={petType} />
      </section>
    </main>
  );
}

function ArchiveCard({ pet }) {
  const href = `/pet?id=${pet.publicNumber}`;
  const playHref = `${href}&play=1`;
  const orderedChoices = stableArchiveChoiceOrder(pet.choices || [], pet.publicNumber);
  return (
    <article className="archive-card">
      <div className="reveal-copy archive-copy">
        <p className="kicker">{pet.publicNumber}</p>
        <h2>{featuredDateLabel(pet)}</h2>
      </div>
      <div className="reveal-detail-grid archive-detail-grid" data-pet-number={pet.publicNumber}>
        <Link className="reveal-photo archive-image-link" href={playHref} aria-label={`Play pet ${pet.publicNumber}`}>
          <PetPhoto pet={pet}
            src={pet.photoUrl || "/assets/favicon.png"}
            srcSet={pet.photoSrcSet || undefined}
            data-pet-number={pet.publicNumber}
            decoding="async"
            loading="lazy"
          />
        </Link>
        <ul className="archive-choice-list" aria-label={`Four name choices for pet ${pet.publicNumber}`}>
          {orderedChoices.map((choice) => <li key={choice.name}>{choice.name}</li>)}
        </ul>
      </div>
      <div className="archive-actions">
        <Link className="button-link archive-play-link" href={playHref}>Play</Link>
        <Link className="archive-reveal-link" href={href}>View Reveal</Link>
      </div>
    </article>
  );
}

function featuredDateLabel(pet) {
  const value = pet.featuredAt || pet.revealedAt;
  if (!value) return `Pet ${pet.publicNumber}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return `Pet ${pet.publicNumber}`;
  return `Featured ${date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`;
}

function PetPage() {
  const params = new URLSearchParams(window.location.search);
  const lookup = params.get("id") || params.get("slug") || "0001";
  const playMode = params.get("play") === "1";
  const submittedGuess = String(params.get("guess") || "").trim();
  const { data: pet, error, retry } = usePublicResource(`/api/pet?id=${encodeURIComponent(lookup)}`);
  const [pendingGuess, setPendingGuess] = useState("");
  const [submittingGuess, setSubmittingGuess] = useState(false);
  const [guessError, setGuessError] = useState("");
  const [resultsView, setResultsView] = useState("feature");
  const playOrderKey = useRef(`past-pet-play-${lookup}-${Date.now()}-${Math.random()}`).current;
  const orderedPlayChoices = useMemo(
    () => stableChoiceOrder(pet?.choices || [], playOrderKey),
    [pet?.id, pet?.choices, playOrderKey],
  );


  usePageMeta(pet);

  if (!pet) {
    return <main><section className="section-band page-shell">
      <LoadState error={error} onRetry={retry}>Loading reveal…</LoadState>
      {error && <Link className="archive-text-link" href="/past-pets">Return to Past Pets</Link>}
    </section></main>;
  }

  if (playMode) {
    const revealHref = `/pet?id=${pet.publicNumber}`;
    const submitGuess = async () => {
      if (!pendingGuess || submittingGuess) return;
      setSubmittingGuess(true);
      setGuessError("");
      pushAnalyticsEvent("past_pet_play_completed", pet.publicNumber);
      try {
        if (!IS_LOCAL_PREVIEW) {
          await apiRequest("/api/past-pet-votes", {
            method: "POST",
            body: JSON.stringify({ pollId: pet.id, choice: pendingGuess, voterKey: getVoterKey() }),
          });
        }
        window.location.assign(`${revealHref}&guess=${encodeURIComponent(pendingGuess)}`);
      } catch (err) {
        setGuessError(err.message || "We couldn't save your guess. Please try again.");
        setSubmittingGuess(false);
      }
    };
    return (
      <main>
        <section className="section-band page-shell">
          <div className="pet-detail-shell">
            <div className="past-pet-play-intro">
              <p className="kicker">Past Pet · {pet.publicNumber}</p>
              <h1>What&rsquo;s my name?</h1>
              {pet.clue && <p className="pet-detail-clue">{pet.clue}</p>}
            </div>
            <div className="past-pet-play-layout">
              <figure className="pet-detail-photo past-pet-play-photo">
                <PetPhoto pet={pet}
                  src={pet.photoUrl || "/assets/favicon.png"}
                  srcSet={pet.photoSrcSet || undefined}
                  data-pet-number={pet.publicNumber}
                  decoding="async"
                  fetchPriority="high"
                />
              </figure>
              <section className="pet-detail-card past-pet-play-card" aria-labelledby="pastPetPlayTitle">
                <h2 id="pastPetPlayTitle">Guess my real name</h2>
                <ChoiceGrid
                  choices={orderedPlayChoices}
                  baseVotes={{}}
                  selected={pendingGuess}
                  busy={submittingGuess}
                  submitted={false}
                  correct=""
                  onSelect={setPendingGuess}
                />
                <div className="past-pet-play-actions">
                  <button className="vote-submit" type="button" disabled={!pendingGuess || submittingGuess} onClick={submitGuess}>
                    {submittingGuess ? "Submitting..." : "Submit Guess"}
                  </button>
                </div>
                {guessError && <p className="form-status error" role="alert">{guessError}</p>}
                <p className="past-pet-play-note">
                  Your vote counts toward What&rsquo;s Winning Now, but the the{" "}
                  <Link className="past-pet-results-link" href={revealHref}>original Feature Day result</Link> stays locked in.
                </p>
              </section>
            </div>
            <Link className="pet-archive-text-link" href="/past-pets">&lt; Return to Past Pets</Link>
          </div>
        </section>
      </main>
    );
  }

  const featureDayChoices = pet.featureDayChoices || pet.choices || [];
  const ongoingChoices = pet.ongoingChoices || featureDayChoices;
  const displayedChoices = resultsView === "ongoing" ? ongoingChoices : featureDayChoices;
  const winner = [...featureDayChoices].sort((a, b) => Number(b.votes || 0) - Number(a.votes || 0))[0];
  const guessedChoice = featureDayChoices.find(
    (choice) => choice.name.toLowerCase() === submittedGuess.toLowerCase(),
  );
  const playNext = pet.playNextPet || pet.nextPet || pet.previousPet;
  return (
    <main>
      <section className="section-band page-shell">
        <div className="pet-detail-shell">
          <div className="pet-detail-hero">
            <div className="pet-detail-copy">
              <p className="kicker">{pet.publicNumber}</p>
              {guessedChoice && (
                <p className={`past-pet-guess-result${guessedChoice.isCorrect ? " correct" : ""}`}>
                  {guessedChoice.isCorrect ? "You got it!" : `You picked ${guessedChoice.name}.`}
                </p>
              )}
              <h1>The answer was {pet.petName}</h1>
              {playNext && <Link className="button-link archive-continue" href={`/pet?id=${playNext.publicNumber}&play=1`}>Play Next Pet</Link>}
              <p className="pet-detail-summary">{pet.correctPercent || 0}% guessed correctly{winner ? `, and ${winner.name} led the Feature Day vote.` : "."}</p>
              {pet.clue && <p className="pet-detail-clue">{pet.clue}</p>}
            </div>
            <figure className="pet-detail-photo">
              <PetPhoto pet={pet}
                src={pet.photoUrl || "/assets/favicon.png"}
                srcSet={pet.photoSrcSet || undefined}
                data-pet-number={pet.publicNumber}
                decoding="async"
                fetchPriority="high"
              />
            </figure>
          </div>
          <section className="pet-detail-card" aria-labelledby="pastPetResultsTitle">
            <h2 id="pastPetResultsTitle">Results</h2>
            <div className="past-pet-results-tabs" role="tablist" aria-label="Past pet result views">
              <button
                type="button"
                role="tab"
                aria-selected={resultsView === "feature"}
                aria-controls="pastPetResultsPanel"
                onClick={() => setResultsView("feature")}
              >
                Feature Day Results
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={resultsView === "ongoing"}
                aria-controls="pastPetResultsPanel"
                onClick={() => setResultsView("ongoing")}
              >
                What&rsquo;s Winning Now
              </button>
            </div>
            <div id="pastPetResultsPanel" className="pet-detail-results" role="tabpanel">
              <ResultsBars
                choices={displayedChoices}
                label={`${pet.petName} ${resultsView === "ongoing" ? "ongoing" : "Feature Day"} voting results`}
                highlightCorrect={false}
                descending
              />
            </div>
            <p className="past-pet-results-note">
              {resultsView === "ongoing"
                ? "Includes the original Feature Day votes plus later guesses."
                : "Results from the pet’s original feature day."}
            </p>
          </section>
          <Link className="pet-archive-text-link" href="/past-pets">&lt; Return to Past Pets</Link>
          {(pet.previousPet || pet.nextPet) && (
            <nav className="pet-neighbor-nav" aria-label="Past pet navigation">
              {pet.previousPet && (
                <Link className="button-link pet-nav-link" href={`${pet.previousPet.url || `/pet?id=${pet.previousPet.publicNumber}`}${(pet.previousPet.url || `/pet?id=${pet.previousPet.publicNumber}`).includes("?") ? "&" : "?"}play=1`}>
                  Play Previous Pet
                </Link>
              )}
              {pet.nextPet && (
                <Link className="button-link pet-nav-link" href={`${pet.nextPet.url || `/pet?id=${pet.nextPet.publicNumber}`}${(pet.nextPet.url || `/pet?id=${pet.nextPet.publicNumber}`).includes("?") ? "&" : "?"}play=1`}>
                  Play Next Pet
                </Link>
              )}
            </nav>
          )}
        </div>
      </section>
    </main>
  );
}

function SubmitPage() {
  const [mode, setMode] = useState("guess");
  const [realName, setRealName] = useState("");
  const [pronouns, setPronouns] = useState("they/them");
  const [petType, setPetType] = useState("Dog");
  const [breed, setBreed] = useState("");
  const [notes, setNotes] = useState("");
  const [email, setEmail] = useState("");
  const [instagramHandle, setInstagramHandle] = useState("");
  const [allowCommunity, setAllowCommunity] = useState(false);
  const [photo, setPhoto] = useState("");
  const [choices, setChoices] = useState(["", "", "", ""]);
  const [lockedSlots, setLockedSlots] = useState(new Set());
  const [status, setStatus] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [photoStatus, setPhotoStatus] = useState("");
  const [preparingPhoto, setPreparingPhoto] = useState(false);
  const [namesStatus, setNamesStatus] = useState("");
  const photoAttemptRef = useRef(0);
  const namesAttemptRef = useRef(0);
  const submitPendingRef = useRef(false);
  const realNameRef = useRef(null);
  const emailRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const formRef = useRef(null);
  const photoInputRef = useRef(null);
  const recentNamesRef = useRef([]);
  const expectedChoices = choices.filter(Boolean).length === 4;
  const canSubmit = Boolean(
    photo
    && (mode === "name" || realName.trim())
    && email.trim()
    && expectedChoices
    && !generating
    && !submitting
    && !preparingPhoto
    && !formErrors.photo,
  );

  async function generateNames(photoValue = photo, currentChoices = choices, currentLocks = lockedSlots) {
    if (!photoValue || (mode === "guess" && !realName.trim())) return;
    const attempt = ++namesAttemptRef.current;
    setGenerating(true);
    setNamesStatus("Generating name choices…");
    const mergeCandidates = (candidateNames) => {
      const nextChoices = Array(4).fill("");
      const used = new Set();
      if (mode === "guess") {
        nextChoices[0] = realName.trim();
        used.add(realName.trim().toLowerCase());
      }
      currentLocks.forEach((index) => {
        const name = currentChoices[index];
        if (name && !(mode === "guess" && index === 0)) {
          nextChoices[index] = name;
          used.add(name.toLowerCase());
        }
      });
      const candidates = [...candidateNames];
      nextChoices.forEach((name, index) => {
        if (name) return;
        const candidateIndex = candidates.findIndex((candidate) => candidate && !used.has(candidate.toLowerCase()));
        if (candidateIndex < 0) return;
        const [candidate] = candidates.splice(candidateIndex, 1);
        nextChoices[index] = candidate;
        used.add(candidate.toLowerCase());
      });
      if (attempt !== namesAttemptRef.current) return;
      setChoices(nextChoices);
      recentNamesRef.current = [...new Set([...nextChoices.filter(Boolean), ...recentNamesRef.current])].slice(0, 500);
    };

    try {
      const result = await apiRequest("/api/name-suggestions", {
        method: "POST",
        timeoutMs: 25_000,
        body: JSON.stringify({
          mode: mode === "name" ? "name" : "guess",
          realName: mode === "guess" ? realName.trim() : "",
          pronouns,
          petType,
          funnyFact: notes.trim(),
          lockedNames: [...currentLocks].map((index) => currentChoices[index]).filter(Boolean),
          recentNames: recentNamesRef.current,
        }),
      });

      const fallbackNames = await fallbackNamesForPronouns(pronouns);
      mergeCandidates([...(result.names || []), ...shuffledNames(fallbackNames)]);
      if (attempt === namesAttemptRef.current) setNamesStatus("Name choices are ready.");
    } catch (error) {
      const fallbackNames = await fallbackNamesForPronouns(pronouns).catch(() => FALLBACK_NAMES);
      mergeCandidates(shuffledNames(fallbackNames));
      if (attempt === namesAttemptRef.current) setNamesStatus("Name choices are ready.");
    } finally {
      if (attempt === namesAttemptRef.current) setGenerating(false);
    }
  }

  function shuffle() {
    generateNames();
  }

  useEffect(() => {
    if (mode === "guess") setChoices((current) => [realName.trim(), ...current.slice(1)]);
  }, [mode, realName]);

  const hasGeneratedDecoys = choices.slice(1).some(Boolean);
  useEffect(() => {
    if (mode !== "guess" || !photo || !realName.trim() || hasGeneratedDecoys || generating || preparingPhoto) return undefined;
    const timeout = window.setTimeout(() => {
      const initialChoices = [realName.trim(), "", "", ""];
      generateNames(photo, initialChoices, new Set());
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [mode, photo, realName, hasGeneratedDecoys, generating, preparingPhoto]);

  useEffect(() => {
    if (!photo || (mode === "guess" && !realName.trim())) return;
    const initialChoices = mode === "guess" ? [realName.trim(), "", "", ""] : ["", "", "", ""];
    generateNames(photo, initialChoices, new Set());
  }, [mode]);

  useEffect(() => {
    if (!photo || (mode === "guess" && !realName.trim())) return;
    const initialChoices = mode === "guess" ? [realName.trim(), "", "", ""] : ["", "", "", ""];
    setChoices(initialChoices);
    setLockedSlots(new Set());
    generateNames(photo, initialChoices, new Set());
  }, [petType, pronouns]);

  async function loadPhoto(file) {
    if (!file || submitPendingRef.current) return;
    const attempt = ++photoAttemptRef.current;
    ++namesAttemptRef.current;
    setGenerating(false);
    setPreparingPhoto(true);
    setFormErrors((current) => ({ ...current, photo: "" }));
    setPhotoStatus("Preparing your photo…");
    try {
      const value = await compressedImageDataUrl(file);
      if (attempt !== photoAttemptRef.current) return;
      const initialChoices = mode === "guess" ? [realName.trim(), "", "", ""] : ["", "", "", ""];
      setPhoto(value);
      setChoices(initialChoices);
      setLockedSlots(new Set());
      setPhotoStatus("Photo ready.");
      setPreparingPhoto(false);
      await generateNames(value, initialChoices, new Set());
    } catch (error) {
      if (attempt !== photoAttemptRef.current) return;
      setFormErrors((current) => ({ ...current, photo: error.message }));
      setPhotoStatus("");
    } finally {
      if (attempt === photoAttemptRef.current) setPreparingPhoto(false);
    }
  }

  function onPhoto(event) {
    loadPhoto(event.target.files?.[0]);
  }

  function onDrop(event) {
    event.preventDefault();
    setDragging(false);
    if (submitting || preparingPhoto) return;
    const file = [...(event.dataTransfer?.files || [])].find(isPetPhotoFile);
    if (!file) {
      setFormErrors((current) => ({ ...current, photo: "Drop an image file to upload a pet photo." }));
      return;
    }
    loadPhoto(file);
  }

  function changeMode(nextMode) {
    setMode(nextMode);
    setStatus("");
    setFormErrors((current) => ({ photo: current.photo }));
    setChoices(nextMode === "guess" ? [realName.trim(), "", "", ""] : ["", "", "", ""]);
    setLockedSlots(new Set());
  }

  function toggleLock(index) {
    if (mode === "guess" && index === 0) return;
    setLockedSlots((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  async function submit(event) {
    event.preventDefault();
    if (submitPendingRef.current || generating || preparingPhoto) return;
    const emailError = emailValidationError(email);
    const errors = {
      realName: mode === "guess" && !realName.trim() ? "Enter your pet’s real name." : "",
      photo: formErrors.photo || (!photo ? "Choose a photo of your pet." : ""),
      email: emailError,
    };
    setFormErrors(errors);
    setStatus("");
    if (errors.realName || errors.photo || errors.email) {
      const field = errors.realName ? realNameRef.current : errors.photo ? photoInputRef.current : emailRef.current;
      field?.focus();
      field?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    if (!canSubmit) {
      setNamesStatus("Name choices aren't ready yet. Select Shuffle names to try again.");
      return;
    }
    submitPendingRef.current = true;
    setSubmitting(true);
    setStatus("");

    if (IS_LOCAL_PREVIEW) {
      sessionStorage.setItem("tpn-preview-submission", JSON.stringify({ mode, choices, photo, realName, breed }));
      navigateTo(`/submission-received?preview=1&mode=${mode}`);
      return;
    }

    try {
      const verificationToken = await turnstileToken(formRef.current);
      const payload = await apiRequest("/api/submissions", {
        method: "POST",
        timeoutMs: 45_000,
        body: JSON.stringify({
          mode: mode === "name" ? "name" : "guess",
          submitterKey: getVoterKey(),
          petName: realName,
          realName,
          ownerEmail: email,
          instagramHandle,
          pronouns,
          petType,
          breed,
          funnyFact: notes.trim(),
          photoDataUrl: photo,
          choices,
          lockedNames: [...lockedSlots].map((index) => choices[index]).filter(Boolean),
          allowCommunityPets: allowCommunity,
          turnstileToken: verificationToken,
        }),
      });
      navigateTo(mode === "name" ? `/community-pet?id=${payload.id}&submitted=1` : `/submission-received?id=${payload.id}`);
    } catch (error) {
      setStatus(error.message);
      submitPendingRef.current = false;
      setSubmitting(false);
      window.tpnTurnstile?.reset?.(formRef.current);
    }
  }

  return (
    <main>
      <section className="section-band page-shell">
        <div className="section-heading submit-heading">
          <div>
            <p className="kicker">Ask the Crowd</p>
            <h1>{mode === "name" ? "Help name my pet" : "Guess my pet's real name"}</h1>
          </div>
          <p className="section-copy">
            {mode === "name"
              ? "Add a photo, and instantly create a public Community Pets poll you can share."
              : "Add the real name and a photo. The game will generate decoy names for everyone else to vote on."}
          </p>
        </div>
        <div className="workbench submit-workbench">
          <form ref={formRef} className="creator-form" noValidate onSubmit={submit} aria-busy={submitting}>
            <fieldset className="form-fields" disabled={submitting || preparingPhoto} aria-label="Pet details">
            <fieldset className="mode-selector" aria-label="Submission type">
              <label><input type="radio" name="submitMode" value="guess" checked={mode === "guess"} onChange={() => changeMode("guess")} /><span>Guess my pet's real name</span></label>
              <label><input type="radio" name="submitMode" value="name" checked={mode === "name"} onChange={() => changeMode("name")} /><span>Help name my pet</span></label>
            </fieldset>
            <div className="field-row">
              {mode === "guess" && <label>Real name<input ref={realNameRef} type="text" placeholder="Snoopy" value={realName} onChange={(event) => { setRealName(capitalizePetName(event.target.value)); setFormErrors((current) => ({ ...current, realName: "" })); }} required aria-invalid={Boolean(formErrors.realName)} aria-describedby="realNameError" /><FieldError id="realNameError">{formErrors.realName}</FieldError></label>}
              <label className="pronouns-field">Pronouns<select value={pronouns} onChange={(event) => setPronouns(event.target.value)}><option>they/them</option><option>she/her</option><option>he/him</option></select></label>
            </div>
            <div className="field-row">
              <label className="pet-type-field">Pet type<select value={petType} onChange={(event) => setPetType(event.target.value)}><option>Dog</option><option>Cat</option><option>Other</option></select></label>
              <label>Breed<input type="text" placeholder="Beagle" autoComplete="off" value={breed} onChange={(event) => setBreed(event.target.value)} /></label>
            </div>
            <div className="upload-field">
              <input ref={photoInputRef} id="reactPhoto" className="upload-input" type="file" accept="image/*,.heic,.heif" onChange={onPhoto} aria-describedby="photoStatus" aria-invalid={Boolean(formErrors.photo)} disabled={preparingPhoto || submitting} />
              <label
                className={`upload-box${photo ? " has-image" : ""}${dragging ? " is-dragging" : ""}`}
                htmlFor="reactPhoto"
                tabIndex="0"
                onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
                onDrop={onDrop}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    photoInputRef.current?.click();
                  }
                }}
              >
                {photo && <PetPhoto pet={{ petType }} context="upload" className="upload-preview-img" src={photo} />}
                {!photo && <span className="upload-icon">+</span>}
                <span className="upload-text">{preparingPhoto ? "Preparing photo…" : photo ? "Change photo" : "Photo"}</span>
              </label>
              <p id="photoStatus" className={formErrors.photo ? "field-error" : "field-hint"} role="status">{formErrors.photo || photoStatus}</p>
            </div>
            <label htmlFor="petNotes">
              What should we know about your pet?
              <span className="field-hint" id="petNotesHint">Think about how they got their name, any obsessions, signature moves, or personality traits.</span>
              <textarea id="petNotes" rows="3" aria-describedby="petNotesHint" value={notes} onChange={(event) => setNotes(event.target.value)} />
            </label>
            <div className="generated-panel" aria-live="polite">
              <div className="generated-header">
                <h3>{mode === "name" ? "Name Picks" : "Name Guesses"}</h3>
                <button type="button" className="shuffle-button" onClick={shuffle} disabled={!photo || generating || preparingPhoto || (mode === "guess" && !realName.trim())}>{generating ? "Generating…" : "Shuffle names"}</button>
              </div>
              <p className="field-hint" role="status">{namesStatus}</p>
              <div className={`mini-options${photo ? "" : " is-pending"}`}>
                {choices.map((choice, index) => {
                  const isTruth = mode === "guess" && index === 0 && Boolean(realName.trim());
                  const displayName = isTruth ? realName.trim() : choice;
                  const isLocked = isTruth || lockedSlots.has(index);
                  return (
                    <div
                      key={`${index}-${choice}`}
                      className={`mini-option${isTruth ? " truth" : ""}${photo ? "" : " placeholder"}`}
                    >
                      <strong className={!photo && !isTruth ? "placeholder-name" : ""}>{displayName}</strong>
                      {displayName && (
                        <button
                          className={`lock-button${isTruth ? " permanent-lock" : ""}`}
                          type="button"
                          disabled={isTruth}
                          aria-pressed={isLocked}
                          aria-label={isTruth ? `${displayName} is the real name and stays locked` : isLocked ? `Unlock ${displayName}` : `Lock ${displayName}`}
                          onClick={() => toggleLock(index)}
                        >
                          <LockIcon locked={isLocked} />
                        </button>
                      )}
                    </div>
                  );
                })}
                {!photo && (
                  <p className="mini-options-note">
                    {mode === "name" ? "Add a photo to generate name picks." : "Add a photo to generate name guesses."}
                  </p>
                )}
              </div>
            </div>
            <label className="owner-email-field">
              Email address
              <input ref={emailRef} type="email" autoComplete="email" value={email} onChange={(event) => { setEmail(event.target.value); setFormErrors((current) => ({ ...current, email: "" })); }} placeholder="you@example.com" required aria-invalid={Boolean(formErrors.email)} aria-describedby="ownerEmailError" />
              <FieldError id="ownerEmailError">{formErrors.email}</FieldError>
              <span className="social-handle-note">You’ll also get our weekly pet email. Unsubscribe anytime.</span>
            </label>
            <label className="instagram-handle-field">
              Instagram handle
              <span className="social-handle-note"><strong>Optional, but encouraged:</strong> Adding your Instagram handle greatly increases your pet’s chances of being featured and may help them be featured sooner. If selected, we’ll tag you and invite you as a collaborator on the reveal post.</span>
              <input type="text" inputMode="text" autoComplete="off" maxLength="31" placeholder="@yourhandle" value={instagramHandle} onChange={(event) => setInstagramHandle(event.target.value)} />
            </label>
            {mode === "guess" && (
              <label className="checkbox-field">
                <input type="checkbox" checked={allowCommunity} onChange={(event) => setAllowCommunity(event.target.checked)} />
                <span>If my pet isn’t selected as a Daily Pet, you can use it in Community Pets.</span>
              </label>
            )}
            <TurnstileField />
            </fieldset>
            <div className="page-actions submit-actions">
              <button type="submit" disabled={submitting || generating || preparingPhoto}>{submitting ? "Submitting..." : "Submit"}</button>
              <p className={`form-status${status ? " error" : ""}`} role="status">{submitting ? "Sending your pet’s details…" : status}</p>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

function SubmissionReceivedPage() {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get("mode") || "guess";
  const preview = params.get("preview") === "1";

  if (mode === "name") {
    return (
      <main>
        <section className="section-band page-shell">
          <article className="legal-page community-live-confirmation">
            <p className="kicker">Submitted</p>
            <h1>Your poll is live</h1>
            <p>{preview ? "This preview does not publish a real poll." : "Share your poll with friends, family, group chats, and social posts so people can vote on the name."}</p>
            <div className="confirmation-links queue-action-links">
              <Link className="button-link" href="/community-pets">Explore Community Pets</Link>
            </div>
            <SubmissionFollowCard />
          </article>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section className="section-band page-shell">
        <article className="legal-page">
          <p className="kicker">Submitted</p>
          <h1>Your pet is in the queue</h1>
          <p>We’ll review your submission for a future Daily Pet.</p>
          <p>If your pet isn’t selected as a Daily Pet and you opted into Community Pets, we may use it in a community poll instead.</p>
          <div className="post-submit-actions" aria-label="Submission next steps">
            <div className="confirmation-links queue-action-links">
              <Link className="button-link" href="/submit">Submit Another Pet</Link>
              <Link className="button-link" href="/community-pets">Explore Community Pets</Link>
            </div>
            <SubmissionFollowCard />
          </div>
        </article>
      </section>
    </main>
  );
}

function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("");
  const [errors, setErrors] = useState({});
  const [tone, setTone] = useState("");
  const [sending, setSending] = useState(false);
  const pendingRef = useRef(false);
  const formRef = useRef(null);
  const emailRef = useRef(null);
  const messageRef = useRef(null);
  function edit(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
    setStatus("");
  }
  async function submit(event) {
    event.preventDefault();
    if (pendingRef.current) return;
    const emailError = emailValidationError(form.email);
    const messageError = form.message.trim() ? "" : "Enter a message so we can help.";
    setErrors({ email: emailError, message: messageError });
    setStatus("");
    if (emailError || messageError) {
      (emailError ? emailRef : messageRef).current?.focus();
      return;
    }
    pendingRef.current = true;
    setSending(true);
    setTone("");
    setStatus("Sending your message…");
    try {
      const verificationToken = await turnstileToken(formRef.current);
      await apiRequest("/api/contact", { method: "POST", body: JSON.stringify({ ...form, turnstileToken: verificationToken }) });
      setStatus("Message sent. Thanks for reaching out.");
      setTone("success");
      setForm({ name: "", email: "", message: "" });
    } catch (error) {
      setStatus(error.message);
      setTone("error");
      window.tpnTurnstile?.reset?.(formRef.current);
    } finally {
      pendingRef.current = false;
      setSending(false);
    }
  }
  return (
    <main>
      <section className="section-band page-shell">
        <div className="contact-shell">
          <div className="legal-page contact-copy"><p className="kicker">Get in touch</p><h1>Contact Us</h1></div>
          <form ref={formRef} className="creator-form contact-form" noValidate onSubmit={submit} aria-busy={sending}>
            <label>Name<input type="text" autoComplete="name" value={form.name} onChange={(event) => edit("name", event.target.value)} disabled={sending} /></label>
            <label>Email<input ref={emailRef} type="email" autoComplete="email" value={form.email} onChange={(event) => edit("email", event.target.value)} required disabled={sending} aria-invalid={Boolean(errors.email)} aria-describedby="contactEmailError" /><FieldError id="contactEmailError">{errors.email}</FieldError></label>
            <label>Message<textarea ref={messageRef} rows="6" value={form.message} onChange={(event) => edit("message", event.target.value)} required disabled={sending} aria-invalid={Boolean(errors.message)} aria-describedby="contactMessageError" /><FieldError id="contactMessageError">{errors.message}</FieldError></label>
            <TurnstileField />
            <button type="submit" disabled={sending}>{sending ? "Sending..." : "Send message"}</button>
            <p className="form-status" data-tone={tone} role="status">{status}</p>
          </form>
        </div>
      </section>
    </main>
  );
}

function FaqPage() {
  const faqs = [
    ["How does the daily vote work?", "Each featured pet gets a public number and four name options. Vote for the name you think is real. Real-time results appear after you vote, but the answer stays hidden until time is up."],
    ["When is the answer revealed?", "The featured pet reveal unlocks after the voting window ends. The previous featured pet appears on the Vote page and is archived on Past Pets."],
    ["What is the difference between a Featured Pet and a Community Pet?", "A Featured Pet appears in the main daily Vote game, receives a locked public number, and gets a reveal page after voting closes. A Community Pet lives in Community Pets and can collect votes right away."],
    ["What is the difference between Guess my pet's real name and Help name my pet?", "Guess my pet's real name is for pets who already have a name. People try to identify the real name from a set of options. Help name my pet is for naming a new pet, so voters are recommending their favorite name rather than guessing a secret answer."],
    ["What happens when I submit my pet for guessing?", "Your submission goes into the review queue. If it is approved and featured, we email you when your pet goes live and again when the final voting results are ready."],
    ["How long will it take to find out if my pet is featured?", "Featured Pet timing depends on the review queue and launch schedule. If your pet is selected, we will email you when it goes live. If you opted into Community Pets and your pet is not selected for the featured game, it may become a Community Pet instead."],
    ["What happens when I ask people to help name my pet?", "A Community Pets poll is created immediately. You get a shareable link so friends, family, and followers can vote on the name picks."],
    ["Can I choose the fake names?", "You can lock generated names you like, but you cannot write in custom decoy names. That keeps the game safer and easier to moderate."],
    ["Will every submitted pet be featured?", "Not necessarily. Featured pets are selected from the review queue. If you opt in, a pet that is not selected as a featured pet may still be used in Community Pets."],
    ["Can I share my pet's poll?", "Yes. Help name my pet submissions get a shareable poll link immediately. Featured Pets can be shared once they are live, and final reveal pages can be shared after voting closes."],
  ];
  return (
    <main>
      <section className="section-band page-shell">
        <div className="legal-page legal-page--left-aligned">
          <p className="kicker">FAQ</p>
          <h1>Frequently Asked Questions</h1>
          {faqs.map(([question, answer]) => <section key={question}><h2>{question}</h2><p>{answer}</p></section>)}
          <section><h2>Can I edit a submission after I send it?</h2><p>Not directly yet. If you need to fix a name, photo, pronouns, or other detail, send a note through the <Link href="/contact">Contact Us</Link> page and include the email you used when submitting.</p></section>
        </div>
      </section>
    </main>
  );
}

function TermsPage() {
  return (
    <main><section className="section-band page-shell"><article className="legal-page legal-page--left-aligned">
      <p className="kicker">Legal</p><h1>Terms of Use</h1><p className="legal-updated">Last updated August 7, 2026</p>
      <h2>Using That Pet Name</h2><p>That Pet Name is a pet-name guessing and naming service. You may vote, submit pets, and participate in name polls as long as you use the site lawfully and respectfully.</p>
      <h2>Submissions</h2><p>When you submit a pet photo or pet details, you confirm that you have the right to share that content. You keep ownership of your submission, but you give That Pet Name a non-exclusive, worldwide, royalty-free license to display, feature, edit, crop, format, reproduce, and share your submission as part of the game, reveal pages, Community Pets polls, emails, social media posts, and promotional or marketing materials for That Pet Name.</p>
      <p>This means, for example, that we may repost or feature submitted pet photos, pet names, vote results, social media handles, and related submission details on That Pet Name websites, Instagram, TikTok, email, or other That Pet Name channels. If you provide a social media handle, we may use it to tag or credit you when sharing your pet. Please do not submit photos or details you do not have permission to share or would not want publicly featured.</p>
      <h2>Moderation</h2><p>We may approve, reject, edit, or remove submissions, names, comments, or other content at our discretion, especially if content is offensive, unsafe, misleading, spammy, or violates another person’s rights.</p>
      <h2>No Guarantees</h2><p>The site is provided as-is. We may change features, pause the game, remove content, or update these terms as the product evolves.</p>
      <h2>Contact</h2><p>Questions about these terms can be sent through the <Link href="/contact">Contact Us</Link> page.</p>
    </article></section></main>
  );
}

function PrivacyPage() {
  return (
    <main><section className="section-band page-shell"><article className="legal-page legal-page--left-aligned">
      <p className="kicker">Privacy</p><h1>Privacy Policy</h1><p className="legal-updated">Last updated September 3, 2026</p>
      <h2>Information We Collect</h2><p>We collect the pet photos, names, pronouns, pet details, notes, email address, and optional social media handles you submit. We also collect votes, reveal and newsletter email signups, and contact messages you choose to send us.</p>
      <p>When you vote on a Featured Pet, we may save privacy-safe first-touch attribution such as campaign tags, the referring website&apos;s domain, the landing-page path, and a random conversion identifier. We do not attach your email address or the full referring URL to the vote.</p>
      <h2>How We Use Information</h2><p>We use this information to run daily guessing games, manage the launch queue, display results, tag or credit submitters in social posts, send reveal updates, respond to messages, and improve the product.</p>
      <h2>Public Content</h2><p>Featured pet submissions may be visible to other visitors and may appear in reveal cards, emails, or social posts. Do not submit photos or details you do not want shared.</p>
      <h2>Email</h2><p>An email address is required with a pet submission so we can contact you about your pet and send important poll or feature updates. Submitting a pet also adds you to our weekly pet email, whether or not your pet is featured. Existing email preferences and opt-outs are respected. If you enter your email for a reveal, join the newsletter, or contact us, we use it for that purpose. You can update email preferences or unsubscribe from the link included in That Pet Name emails.</p>
      <p>Our newsletter and other marketing emails may use a small image and redirected links supplied by our email provider, Resend, to measure deliveries, opens, and clicks. Open measurements can be affected by email-app privacy features. We use these aggregate measurements to improve our emails. Transactional messages such as requested reveals and pet-submission updates do not use open or click tracking.</p>
      <h2>Analytics and Advertising Technologies</h2>
      <p>Plausible Analytics runs on every visit. We use Plausible to count visits and understand which pages and features people use. Plausible is configured without cookies or persistent cross-site identifiers. Learn more in <a href="https://plausible.io/data-policy">Plausible&apos;s data policy</a>.</p>
      <p>The Meta Pixel and Reddit Pixel run by default. You can turn them off through <strong>Privacy Choices</strong> in the site footer. They help us measure advertising and actions taken on the site. These services may use cookies and receive information about your browser or device, the pages you visit, and your interactions with the site. Learn more in the <a href="https://www.facebook.com/privacy/policy/">Meta Privacy Policy</a> and <a href="https://www.reddit.com/policies/privacy-policy">Reddit Privacy Policy</a>.</p>
      <p>Google Tag Manager delivers and configures these services. Plausible and advertising tags run by default. Advertising tags do not run when you have saved an advertising opt-out.</p>
      <h2>Your Privacy Choices</h2>
      <p>We store your choice in your browser&apos;s local storage so the site can remember it. This record contains the choice, a consent-policy version, and the time it was saved. It is not used for advertising.</p>
      <p>Essential and Analytics Cookies are always enabled. Use <a href="#manage-cookie-preferences" onClick={(event) => { event.preventDefault(); window.tpnConsent?.open(); }}>Privacy Choices</a> in the site footer to allow or turn off Advertising &amp; Targeting Cookies. Turning them off stops future advertising tags, removes known first-party marketing cookies, and reloads the page to unload advertising code that was already running.</p>
      <h2>Data Choices</h2><p>To ask about removing a submission or contact message, use the <Link href="/contact">Contact Us</Link> page.</p>
    </article></section></main>
  );
}

function EmailPreferencesPage() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") || "", []);
  const [email, setEmail] = useState("");
  const [frequency, setFrequency] = useState("weekly");
  const [petPreference, setPetPreference] = useState("all");
  const [status, setStatus] = useState("");
  const [tone, setTone] = useState("");
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [operation, setOperation] = useState("");
  const pendingRef = useRef(false);

  function renderStatus(message, nextTone = "") {
    setStatus(message);
    setTone(nextTone);
  }

  useEffect(() => {
    let mounted = true;
    async function loadPreference() {
      if (!token) {
        renderStatus("This preference link is missing or invalid.", "error");
        setLoading(false);
        return;
      }
      setLoading(true);
      renderStatus("Loading your preferences…");
      try {
        const preference = await apiRequest(`/api/email-preferences?token=${encodeURIComponent(token)}`);
        if (!mounted) return;
        setLoaded(true);
        setEmail(preference.email || "");
        setFrequency(["all", "weekly", "monthly"].includes(preference.emailFrequency) ? preference.emailFrequency : "weekly");
        setPetPreference(["all", "cats", "dogs"].includes(preference.petPreference) ? preference.petPreference : "all");
        renderStatus(preference.emailStatus === "unsubscribed" ? "You are unsubscribed from That Pet Name emails." : "", preference.emailStatus === "unsubscribed" ? "success" : "");
      } catch (error) {
        if (mounted) renderStatus(error.message, "error");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadPreference();
    return () => {
      mounted = false;
    };
  }, [token, loadAttempt]);

  async function submitPreference(event) {
    event.preventDefault();
    if (pendingRef.current || !loaded) return;
    pendingRef.current = true;
    setOperation("save");
    renderStatus("Saving your preferences…");
    try {
      setLoading(true);
      const preference = await apiRequest("/api/email-preferences", {
        method: "POST",
        body: JSON.stringify({ token, action: "update", emailFrequency: frequency, petPreference }),
      });
      setEmail(preference.email || email);
      setFrequency(["all", "weekly", "monthly"].includes(preference.emailFrequency) ? preference.emailFrequency : "weekly");
      setPetPreference(["all", "cats", "dogs"].includes(preference.petPreference) ? preference.petPreference : "all");
      renderStatus("Your email preferences have been updated.", "success");
    } catch (error) {
      renderStatus(error.message, "error");
    } finally {
      pendingRef.current = false;
      setOperation("");
      setLoading(false);
    }
  }

  async function unsubscribe() {
    if (pendingRef.current || !loaded) return;
    pendingRef.current = true;
    setOperation("unsubscribe");
    renderStatus("Updating your email preferences…");
    try {
      setLoading(true);
      const preference = await apiRequest("/api/email-preferences", {
        method: "POST",
        body: JSON.stringify({ token, action: "unsubscribe" }),
      });
      setEmail(preference.email || email);
      renderStatus("You have been unsubscribed from That Pet Name emails.", "success");
    } catch (error) {
      renderStatus(error.message, "error");
    } finally {
      pendingRef.current = false;
      setOperation("");
      setLoading(false);
    }
  }

  return (
    <main>
      <section className="section-band page-shell preference-shell">
        <div className="preference-heading">
          <p className="kicker">Email Preferences</p>
          <h1>Set your email preferences</h1>
          <p className="preference-intro">Choose which pets you want to see and how often they arrive in your inbox.</p>
        </div>
        <form className="preference-form" onSubmit={submitPreference} aria-busy={loading}>
          <div className="preference-row">
            <label className="preference-question" htmlFor="preferenceEmail">Email address</label>
            <input id="preferenceEmail" name="email" type="email" autoComplete="email" readOnly required value={email} />
          </div>
          <div className="preference-row preference-options" role="group" aria-labelledby="petPreferenceLabel">
            <h2 className="preference-question" id="petPreferenceLabel">Pet preference</h2>
            <div className="preference-choice-list">
              <label><input type="radio" name="petPreference" value="all" checked={petPreference === "all"} onChange={() => setPetPreference("all")} disabled={loading || !loaded} /><span>All Pets</span></label>
              <label><input type="radio" name="petPreference" value="cats" checked={petPreference === "cats"} onChange={() => setPetPreference("cats")} disabled={loading || !loaded} /><span>Cats</span></label>
              <label><input type="radio" name="petPreference" value="dogs" checked={petPreference === "dogs"} onChange={() => setPetPreference("dogs")} disabled={loading || !loaded} /><span>Dogs</span></label>
            </div>
          </div>
          <div className="preference-row preference-options" role="group" aria-labelledby="emailFrequencyLabel">
            <h2 className="preference-question" id="emailFrequencyLabel">How often would you like to hear from us?</h2>
            <div className="preference-choice-list">
              <label><input type="radio" name="emailFrequency" value="all" checked={frequency === "all"} onChange={() => setFrequency("all")} disabled={loading || !loaded} /><span>Receive All Emails</span></label>
              <label><input type="radio" name="emailFrequency" value="weekly" checked={frequency === "weekly"} onChange={() => setFrequency("weekly")} disabled={loading || !loaded} /><span>Once per week</span></label>
              <label><input type="radio" name="emailFrequency" value="monthly" checked={frequency === "monthly"} onChange={() => setFrequency("monthly")} disabled={loading || !loaded} /><span>Once per month</span></label>
            </div>
          </div>
          <div className="preference-actions">
            <button type="submit" disabled={loading || !loaded}>{operation === "save" ? "Saving…" : "Update Preferences"}</button>
            <button className="preference-unsubscribe" type="button" onClick={unsubscribe} disabled={loading || !loaded}>{operation === "unsubscribe" ? "Updating…" : "Unsubscribe me from all emails"}</button>
          </div>
          {!loaded && !loading && token && <button type="button" onClick={() => setLoadAttempt((value) => value + 1)}>Try again</button>}
          <p className="form-status" data-tone={tone} role="status" aria-live="polite">{status}</p>
        </form>
      </section>
    </main>
  );
}

function NotFoundPage() {
  return <main className="not-found-page">
    <p className="kicker">404 · Page not found</p>
    <h1>This page wandered off.</h1>
    <p>There’s still a pet waiting for your best guess. Let’s get you back to the game.</p>
    <div className="not-found-actions">
      <Link className="button-link" href="/">Play Today’s Game</Link>
      <Link className="button-link" href="/past-pets">Explore Past Pets</Link>
      <Link className="button-link" href="/submit">Submit a Pet</Link>
    </div>
  </main>;
}

function App() {
  const location = useRoute();
  usePageMeta();
  const route = location.route;
  const page = {
    vote: <VotePage key={location.key} />,
    pet0000: <VotePage key={location.key} demoPet={PET0000_DEMO_PET} />,
    welcome: <WelcomePage key={location.key} />,
    subscribe: <SubscribePage key={location.key} />,
    reveal: <SubscribePage key={location.key} reveal />,
    submit: <SubmitPage key={location.key} />,
    past: <PastPetsPage key={location.key} />,
    pet: <PetPage key={location.key} />,
    community: <CommunityPetsPage key={location.key} />,
    communityPet: <CommunityPetPage key={location.key} />,
    contact: <ContactPage key={location.key} />,
    faq: <FaqPage key={location.key} />,
    terms: <TermsPage key={location.key} />,
    privacy: <PrivacyPage key={location.key} />,
    emailPreferences: <EmailPreferencesPage key={location.key} />,
    submissionReceived: <SubmissionReceivedPage key={location.key} />,
    admin: <Suspense fallback={<main><section className="section-band page-shell"><p className="admin-empty">Loading admin...</p></section></main>}><AdminPage key={location.key} /></Suspense>,
    notFound: <NotFoundPage />,
  }[route] || <NotFoundPage />;

  return (
    <>
      <Header route={route} />
      {page}
      <Footer />
    </>
  );
}

createRoot(document.querySelector("#root")).render(<App />);
